/**
 * TaxMe - Belgian Capital Gains Tracker - Proprietary Software
 * Copyright (c) 2025. All Rights Reserved.
 * 
 * This source code is confidential and proprietary.
 * Unauthorized copying or distribution is strictly prohibited.
 * For licensing inquiries: Use the Share Feedback form in the application
 */


import React, { useState, useEffect, useRef, useMemo } from 'react';
import './App.css';
import { SignInButton, UserButton, SignedIn, SignedOut, useUser } from '@clerk/clerk-react';
import ContactFormModal from './components/ContactFormModal';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

// Theme Icons as React Components
const SunIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2"/>
    <path d="M12 2v2M12 20v2M22 12h-2M4 12H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M19.07 4.93l-1.41 1.41M6.34 17.66l-1.41 1.41M19.07 19.07l-1.41-1.41M6.34 6.34L4.93 4.93" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const MoonIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.32031 11.6835C3.32031 16.6541 7.34975 20.6835 12.3203 20.6835C16.1075 20.6835 19.3483 18.3443 20.6768 15.032C19.6402 15.4486 18.5059 15.6834 17.3203 15.6834C12.3497 15.6834 8.32031 11.654 8.32031 6.68342C8.32031 5.50338 8.55165 4.36259 8.96453 3.32996C5.65605 4.66028 3.32031 7.89912 3.32031 11.6835Z" 
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

function App() {
  // Runtime localStorage availability check
  const [localStorageAvailable] = useState(() => {
    try {
      const testKey = 'taxme_runtime_test';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      return false;
    }
  });
  
// Helper function to parse various date formats
const parseIBDate = (dateStr) => {
  // Return null for missing/invalid input
  if (!dateStr || dateStr === null || dateStr === 'null' || dateStr === 'undefined') {
    return null;
  }
  
   // Clean and validate
  dateStr = String(dateStr).trim();
  if (dateStr === '') {
    return null;
  }
  
  // Already a Date object?
  if (dateStr instanceof Date) return dateStr;
  
  // Try different formats
  let date;
  
  // ISO format (YYYY-MM-DD) or with slashes (YYYY/MM/DD)
  if (dateStr.match(/^\d{4}[-/]\d{2}[-/]\d{2}$/)) {
    date = new Date(dateStr);
  }
  // European format (DD-MM-YYYY or DD/MM/YYYY)
  else if (dateStr.match(/^\d{2}[-/]\d{2}[-/]\d{4}$/)) {
    const parts = dateStr.split(/[-/]/);
    date = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
  }
  // IB compact format (YYYYMMDD)
  else if (dateStr.match(/^\d{8}$/)) {
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    date = new Date(`${year}-${month}-${day}`);
  }
  // ISO datetime (YYYY-MM-DDTHH:mm:ss)
  else if (dateStr.includes('T')) {
    date = new Date(dateStr);
  }
  // Last resort - let JS try to parse it
  // Last resort - reject unparseable dates rather than risk browser-dependent parsing
  else {
    console.warn(`Unrecognized date format: ${dateStr}`);
    return null;
  }
  
  // Check if valid date
  if (!date || isNaN(date.getTime())) {
    console.warn(`Could not parse date: ${dateStr}`);
    return null;  // <- THE FIX
  }
  
  // Add year sanity check
  const year = date.getFullYear();
  if (year < 1990 || year > 2100) {
    console.warn(`Suspicious year ${year} in date: ${dateStr}`);
    return null;
  }
  
  return date;
};


  // Unified year extraction - single source of truth for available years
    const getAvailableYearsFromTrades = (trades) => {
      if (!trades || trades.length === 0) return [];
      
      const yearsSet = new Set();
      
      trades.forEach(trade => {
        const tradeDate = trade.TradeDate || trade.tradeDate;
        if (tradeDate) {
          const dateObj = parseIBDate(tradeDate);
          
          // ADD NULL CHECK HERE
          if (!dateObj) {
            console.warn(`Skipping invalid date in year extraction: ${tradeDate}`);
            return; // Skip this trade
          }
          
          const year = dateObj.getFullYear();
          
          if (!isNaN(year) && year >= 2000 && year <= 2100) {
            yearsSet.add(year.toString());
          }
        }
      });
    
      return Array.from(yearsSet).sort();
    };

  // ========== STATE MANAGEMENT ==========
  // State for the December 31 positions snapshot
   const [positionsData, setPositionsData] = useState([]);         // Parsed positions array
  // State for monthly trade imports
   const [tradesData, setTradesData] = useState([]);          // Parsed trades array
  // State for showing load status
  const [dataLoadStatus, setDataLoadStatus] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // Start true for initial load
  const [loadingMessage, setLoadingMessage] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [storageInfo, setStorageInfo] = useState({ used: 0, percentage: 0 });
  const [controlPanelExpanded, setControlPanelExpanded] = useState(false);
   const [userCurrency, setUserCurrency] = useState(() => {
  try {
    return localStorage.getItem('taxme_userCurrency') || 'EUR';
  } catch {
    return 'EUR';
  }
});
 const [currencySource, setCurrencySource] = useState(() => {
  try {
    return localStorage.getItem('taxme_currencySource') || null;
  } catch {
    return null;
  }
});
  // Theme state
  const [theme, setTheme] = useState(() => {
    try {
      const savedTheme = localStorage.getItem('taxme_theme');
      if (savedTheme) return savedTheme;
    } catch {
      // localStorage unavailable, fall through to system preference
    }
    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  const [activeTab, setActiveTab] = useState(0);  // 0 = first tab (Overview)
const [lastSaveTime, setLastSaveTime] = useState(() => {
  try {
    return localStorage.getItem('taxme_lastSaveTime') || null;
  } catch {
    return null;
  }
});
 // Sorting state for tables
  const [sortColumn, setSortColumn] = useState('symbol');  // Which column is sorted
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  // State for calculated holdings data
  const [holdingsData, setHoldingsData] = useState({
  holdings: [],
  lots: {},
  sellTransactions: []
});
const [showContactForm, setShowContactForm] = useState(false);

// State for tax calculations
const [taxData, setTaxData] = useState({
  taxableTransactions: [],
  countryCode: 'BE',
  rulesVersion: '2024-2026',
  transactionsByYear: {},
  summaries: {}  
});

const [selectedYears, setSelectedYears] = useState(() => {
  try {
    const saved = localStorage.getItem('taxme_selectedYears');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Ensure single element, no ALL
      if (Array.isArray(parsed) && parsed.length > 0 && parsed[0] !== 'ALL') {
        return [parsed[0]]; // Take only first element
      }
    }
  } catch (error) {
    console.error('Error parsing saved years:', error);
    localStorage.removeItem('taxme_selectedYears');
  }
  return []; // Start with empty, will auto-select when data loads
});

// Cache available years to avoid recalculating
const availableYears = useMemo(() => 
  getAvailableYearsFromTrades(tradesData), 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [tradesData]  // Only recalculate when trades change, not when function changes
);

const hasAutoSelectedYear = useRef(false);

//Scroll-triggered animation Three Simple Steps
const [hasAnimated, setHasAnimated] = useState(false);
const guidelinesRef = useRef(null);

//Scroll-triggered animation USPs
const [uspAnimated, setUspAnimated] = useState(false);
const uspSectionRef = useRef(null);

// Belgian CGT Tax Configuration: Exemption amounts are indexed annually to cost of living. Rate and transition end year may change with future legislation
const TRANSITION_END_YEAR = 2030;
const TAX_CONFIG = {
  2026: { exemption: 10000, rate: 0.10 },
};

// Helper to get config for a year (falls back to most recent known values)
const getTaxConfig = (year) => {
  if (TAX_CONFIG[year]) {
    return TAX_CONFIG[year];
  }
  // Fallback: use last known config
  const knownYears = Object.keys(TAX_CONFIG).map(Number).sort((a, b) => b - a);
  const fallbackYear = knownYears.find(y => y <= year) || knownYears[0];
  return TAX_CONFIG[fallbackYear];
};


// Sample data for demo purposes
const SAMPLE_DATA = {
  positions: [
    {
      Symbol: "AAPL",
      Quantity: "50",
      MarkPrice: "195.50",
      PositionValue: "9775.00",
      CostBasisPrice: "150.00",
      CostBasisMoney: "7500.00",
      CurrencyPrimary: "USD",
      FXRateToBase: "0.92",
      AssetClass: "STK",
      ReportDate: "20251231"
    },
    {
      Symbol: "ASML",
      Quantity: "15",
      MarkPrice: "680.00",
      PositionValue: "10200.00",
      CostBasisPrice: "720.00",
      CostBasisMoney: "10800.00",
      CurrencyPrimary: "EUR",
      FXRateToBase: "1",
      AssetClass: "STK",
      ReportDate: "20251231"
    },
    {
      Symbol: "NVDA",
      Quantity: "30",
      MarkPrice: "135.00",
      PositionValue: "4050.00",
      CostBasisPrice: "110.00",
      CostBasisMoney: "3300.00",
      CurrencyPrimary: "USD",
      FXRateToBase: "0.92",
      AssetClass: "STK",
      ReportDate: "20251231"
    },
    {
      Symbol: "BTC-USD",
      Quantity: "0.5",
      MarkPrice: "98000.00",
      PositionValue: "49000.00",
      CostBasisPrice: "45000.00",
      CostBasisMoney: "22500.00",
      CurrencyPrimary: "USD",
      FXRateToBase: "0.92",
      AssetClass: "CRYPTO",
      ReportDate: "20251231"
    },
    {
      Symbol: "SPY C450 20270115",
      Quantity: "10",
      MarkPrice: "25.50",
      PositionValue: "25500.00",
      CostBasisPrice: "18.00",
      CostBasisMoney: "18000.00",
      CurrencyPrimary: "USD",
      FXRateToBase: "0.92",
      AssetClass: "OPT",
      ReportDate: "20251231",
      Multiplier: "100"
    }
  ],
  trades: [
    // Some 2026 trades to show tax impact
    {
      TradeID: "DEMO001",
      Symbol: "AAPL",
      TradeDate: "20260120",
      "Buy/Sell": "SELL",
      Quantity: "25",
      TradePrice: "198.00",
      TradeMoney: "4950.00",
      CurrencyPrimary: "USD",
      FXRateToBase: "0.91",
      AssetClass: "STK"
    },
    {
      TradeID: "DEMO002",
      Symbol: "MSFT",
      TradeDate: "20260125",
      "Buy/Sell": "BUY",
      Quantity: "40",
      TradePrice: "420.00",
      TradeMoney: "16800.00",
      CurrencyPrimary: "USD",
      FXRateToBase: "0.91",
      AssetClass: "STK"
    },
    {
      TradeID: "DEMO003",
      Symbol: "ASML",
      TradeDate: "20260205",
      "Buy/Sell": "SELL",
      Quantity: "5",
      TradePrice: "695.00",
      TradeMoney: "3475.00",
      CurrencyPrimary: "EUR",
      FXRateToBase: "1",
      AssetClass: "STK"
    },
    {
      TradeID: "DEMO004",
      Symbol: "MSFT",
      TradeDate: "20260315",
      "Buy/Sell": "SELL",
      Quantity: "40",
      TradePrice: "445.00",
      TradeMoney: "17800.00",
      CurrencyPrimary: "USD",
      FXRateToBase: "0.90",
      AssetClass: "STK"
    },
    {
      TradeID: "DEMO005",
      Symbol: "TSLA",
      TradeDate: "20260609",
      "Buy/Sell": "BUY",
      Quantity: "50",
      TradePrice: "180.00",
      TradeMoney: "9000.00",
      CurrencyPrimary: "USD",
      FXRateToBase: "0.89",
      AssetClass: "STK"
    },
    {
      TradeID: "DEMO006",
      Symbol: "NVDA",
      TradeDate: "20260501",
      "Buy/Sell": "BUY",
      Quantity: "15",
      TradePrice: "142.00",
      TradeMoney: "2130.00",
      CurrencyPrimary: "USD",
      FXRateToBase: "0.90",
      AssetClass: "STK"
    },
    {
      TradeID: "DEMO007",
      Symbol: "SPY C450 20270115",
      TradeDate: "20260612",
      "Buy/Sell": "SELL",
      Quantity: "5",
      TradePrice: "28.00",
      TradeMoney: "14000.00",
      CurrencyPrimary: "USD",
      FXRateToBase: "0.89",
      AssetClass: "OPT",
      Multiplier: "100"
    },
    {
      TradeID: "DEMO008",
      Symbol: "BTC-USD",
      TradeDate: "20260710",
      "Buy/Sell": "SELL",
      Quantity: "0.25",
      TradePrice: "105000.00",
      TradeMoney: "26250.00",
      CurrencyPrimary: "USD",
      FXRateToBase: "0.88",
      AssetClass: "CRYPTO"
    }
  ]
};

// AUTO-SELECT YEAR WHEN TRADES DATA CHANGES
// This effect ensures a year is always selected for filtering, preventing empty views.
// It runs whenever tradesData updates (from file import or localStorage load) and only
// acts if no year is currently selected. This solves the UX issue where users would see
// empty Tax/Trade tabs because selectedYears was [], even though data exists.
// 
// Trigger: tradesData changes (length goes from 0 to n)
// Action: Auto-select the most recent year from available data
// Guard: Only runs if selectedYears is empty to avoid overriding user selection
useEffect(() => {
  // Only run if:
  // 1. We have trades data
  // 2. No year is currently selected
  // 3. We haven't already auto-selected a year
  if (tradesData.length > 0 && 
      (selectedYears === null || selectedYears.length === 0) && 
      !hasAutoSelectedYear.current) {
    
    const availableYears = getAvailableYearsFromTrades(tradesData);
    
    if (availableYears.length > 0) {
      console.log('Auto-selecting oldest year:', availableYears[0]);
      setSelectedYears([availableYears[0]]);
      hasAutoSelectedYear.current = true; // Mark that we've auto-selected
    }
  }
  
  // Reset the flag if trades are cleared
  if (tradesData.length === 0) {
    hasAutoSelectedYear.current = false;
  }
}, [tradesData.length, selectedYears]); // eslint-disable-line react-hooks/exhaustive-deps

const [currentView, setCurrentView] = useState('app'); // 'app', 'terms', 'privacy', or 'help'



// Clerk hook
  const { isSignedIn } = useUser();

// Export to Excel function
const exportToExcel = () => {
    // Check if we have data to export
  if (!selectedYears || !selectedYears[0] || !taxData.summaries || !taxData.summaries[selectedYears[0]]) {
    alert('No tax data to export for the selected year');
    return;
  }

  // Get current year's data
  const yearData = taxData.summaries[selectedYears[0]];

  // Get transactions for the selected year
  const yearTransactions = taxData.transactionsByYear && taxData.transactionsByYear[selectedYears[0]]
    ? taxData.transactionsByYear[selectedYears[0]]
    : [];
  
  // Create workbook
  const wb = XLSX.utils.book_new();
    
  // Sheet 1: Summary data - matching EXACTLY what the UI shows
  const summaryData = [
    ['Tax Summary Report'],
    [],
    ['Year', selectedYears[0]],
    ['Generated', new Date().toLocaleString()],
    [],
    ['Tax Calculation'],
    ['Total Realized Gains', formatCurrency(yearData.totalRealizedGains || 0)],
    ['Taxable Portion', formatCurrency(yearData.net || 0)],
    ['Annual Exemption', formatCurrency(yearData.exemptionUsed || 0)],
    ['Subject to Tax', formatCurrency(yearData.taxable || 0)],
    ['Tax Due (10%)', formatCurrency(yearData.taxDue || 0)],
    [],
    ['Transaction Details'],
    ['Number of Transactions', yearTransactions.length]
  ];
  
  const ws_summary = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(wb, ws_summary, "Summary");
  
  // Sheet 2: Transactions detail - matching the table columns exactly
  const transactionsData = [
    ['Instrument', 'Quantity', 'Actual P&L', 'Amount Subject to Tax', 'YTD Cumulative Taxable', 'Tax Rule', 'Purchase Date']
  ];
  
  // Add each transaction as a row
  yearTransactions.forEach(transaction => {
    transactionsData.push([
      transaction.symbol || '',
      transaction.quantity || 0,
      formatCurrency(transaction.realGainLoss || 0),
      formatCurrency(transaction.taxableGainLoss || 0),
      formatCurrency(transaction.runningNet || 0),
      transaction.taxRuleStatus || '',
      formatDateForDisplay(transaction.tradeDate) || ''
    ]);
  });
  
  const ws_transactions = XLSX.utils.aoa_to_sheet(transactionsData);
  XLSX.utils.book_append_sheet(wb, ws_transactions, "Transactions");
  
// Generate timestamp in YYYYMMDD format
const now = new Date();
const timestamp = now.getFullYear() + 
                  String(now.getMonth() + 1).padStart(2, '0') + 
                  String(now.getDate()).padStart(2, '0');

// Updated filename with timestamp
const filename = `TaxMe_Report_${selectedYears[0]}_${timestamp}.xlsx`;
  
  // Download the file
  XLSX.writeFile(wb, filename);
};


// Filter trades to show only the single selected year
const getFilteredTrades = () => {
  if (!selectedYears || selectedYears.length === 0 || !selectedYears[0]) return tradesData;// Return all if no year selected yet
  
  return tradesData.filter(t => {
    // Skip trades with missing dates
    if (!t.TradeDate || t.TradeDate === null || t.TradeDate === '') {
      console.warn('Trade missing date:', t.TradeID || t.Symbol);
      return false; // Don't include in filtered results
    }
    
    try {
      const year = parseIBDate(t.TradeDate).getFullYear().toString();
      return year === selectedYears[0]; // Compare to the single selected year
    } catch (error) {
      console.error('Error parsing trade date:', t.TradeDate, error);
      return false; // Don't include if date parsing fails
    }
  });
};

// Three circles animation - replays on re-entry
useEffect(() => {
  // Capture the element reference at effect run time
  const element = guidelinesRef.current;
  
  if (!element) return; // Early exit if no element
  
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setHasAnimated(true);
        } else {
          // Reset when leaving viewport so it can replay
          setHasAnimated(false);
        }
      });
    },
    {
      threshold: 0.3,
      rootMargin: '0px'
    }
  );

  // Observe the captured element
  observer.observe(element);

  // Cleanup: unobserve the specific element we observed
  return () => {
    observer.unobserve(element);
    observer.disconnect();
  };
}, []); // Empty dependency array - observer stays active

// USP icons animation - replays on re-entry
useEffect(() => {
  // Capture the element reference at effect run time
  const element = uspSectionRef.current;
  
  if (!element) return; // Early exit if no element
  
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setUspAnimated(true);
        } else {
          // Reset when leaving viewport so it can replay
          setUspAnimated(false);
        }
      });
    },
    {
      threshold: 0.3,
      rootMargin: '0px'
    }
  );

  // Observe the captured element
  observer.observe(element);

  // Cleanup: unobserve the specific element we observed
  return () => {
    observer.unobserve(element);
    observer.disconnect();
  };
  }, []);

// Prevent body scroll when mobile menu is open
useEffect(() => {
  if (mobileMenuOpen) {
    document.body.classList.add('mobile-menu-open');
  } else {
    document.body.classList.remove('mobile-menu-open');
  }
  
  return () => {
    document.body.classList.remove('mobile-menu-open');
  };
}, [mobileMenuOpen]);

// Auto-collapse control panel when both datasets are loaded
useEffect(() => {
  // Collapse if we have trades (sufficient to work)
  if (tradesData.length > 0) {
    setControlPanelExpanded(false);
  } else {
    setControlPanelExpanded(true);
  }
}, [tradesData.length]);


// Pulse animation when triangles first appear
useEffect(() => {
  // Check if both datasets just loaded (triangles should be visible)
  const bothLoaded = positionsData.length > 0 && tradesData.length > 0;
  
  if (bothLoaded) {
    // Find the triangle elements
    const triangles = document.querySelectorAll('.control-panel-toggle .triangle');
    
    if (triangles.length === 2) {
      // Add pulse animation to left triangle (first one)
      triangles[0].classList.add('pulse-left');
      
      // Add pulse animation to right triangle (second one) after delay
      const rightTimeout = setTimeout(() => {
        triangles[1].classList.add('pulse-right');
      }, 200); // 200ms delay
      
      // Remove animation classes after animations complete
      const cleanupTimeout = setTimeout(() => {
        triangles[0].classList.remove('pulse-left');
        triangles[1].classList.remove('pulse-right');
      }, 1000); // Remove after 1 second (animation + delay)
      
      // Cleanup timeouts on unmount
      return () => {
        clearTimeout(rightTimeout);
        clearTimeout(cleanupTimeout);
      };
    }
  }
}, [positionsData.length, tradesData.length]); // Run when data changes





// Calculate holdings when source data changes
useEffect(() => {
  console.log('useEffect running. Positions:', positionsData.length, 'Trades:', tradesData.length);
    
  if (tradesData.length > 0) {
    // Create checksum of current trade data
    const getTradesChecksum = (trades) => {
      return trades.map(t => 
        `${t.TradeDate}-${t.Symbol}-${t.Quantity}-${t.TradeMoney}-${t['Buy/Sell']}`
      ).sort().join('|'); // Sort to ensure consistent order
    };
    
    const currentTradesChecksum = getTradesChecksum(tradesData);
    
    // Check if we have saved processed data
    const savedData = localStorage.getItem('processedHoldings');
    
    if (savedData) {
      const parsed = JSON.parse(savedData);
      console.log('Found saved processed data from:', parsed.lastProcessed);
      
      // Check if trades have changed by comparing checksums
      if (parsed.tradesChecksum !== currentTradesChecksum) {
        console.log('Trades data changed (checksum mismatch), recalculating...');
        localStorage.removeItem('processedHoldings');
        // Don't return - let it fall through to recalculation
      } else {
        // Trades haven't changed, use saved data
        console.log('Using saved processed data (checksum match)');
        setHoldingsData({
          holdings: parsed.holdings || [],
          lots: parsed.lots || {},
          sellTransactions: parsed.sellTransactions || [],
          buyTransactions: parsed.buyTransactions || []
        });
        
        return; // Don't recalculate if checksum matches
      }
    } else {
      console.log('No saved processed data found');
    }
    
    // Calculate holdings (either no saved data or checksum mismatch)
    console.log('Calculating holdings data...');
    const result = calculateCurrentHoldings(positionsData, tradesData);
    const tradingMetrics = calculateTradingMetrics(result.sellTransactions, result.buyTransactions);
    const enhancedResult = {...result, sellTransactions: tradingMetrics.transactions};
    
    
    setHoldingsData(enhancedResult);

   if (enhancedResult.errors && Array.isArray(enhancedResult.errors) && enhancedResult.errors.length > 0) {
  enhancedResult.errors.forEach(error => {
    if (error.type === 'OVERSELLING') {
      showNotification(
        `⚠️ Data Issue: Tried to sell ${error.attempted} shares of ${error.symbol} on ${formatDateForDisplay(error.tradeDate)}, but only ${error.available} shares were available. Please check your trade history.`,
        'error'
      );
    }
  });
}
    
    // Save processed data with checksum
    if (enhancedResult.holdings.length > 0) {
      const dataToSave = {
        holdings: enhancedResult.holdings,
        lots: enhancedResult.lots,
        sellTransactions: enhancedResult.sellTransactions,
        buyTransactions: enhancedResult.buyTransactions,
        tradesChecksum: currentTradesChecksum, // Save the checksum
        lastProcessed: new Date().toISOString(),
        version: "1.0"
      };
      const saveResult = safeLocalStorageSave('processedHoldings', JSON.stringify(dataToSave));
      if (saveResult.success) {
        console.log('Auto-saved processed holdings data with checksum');
      }
    }
  } else {
    setHoldingsData({
      holdings: [],
      lots: {},
      sellTransactions: [],
      buyTransactions: []
    });
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [positionsData, tradesData, userCurrency]);


// AUTO-INITIALIZE YEAR SELECTION BASED ON AVAILABLE DATA
// This logic runs after trades are loaded from localStorage and ensures the year
// selector defaults to a meaningful value based on actual data rather than blindly
// using the current calendar year.
// 
// Initialization priority:
// 1. If selectedYears already has a value (from localStorage), keep it
// 2. Otherwise, extract available years from loaded trades
// 3. Default to the most recent year found in the data
// 
// This prevents the common UX issue where users load the app in 2025 but their
// trades are from 2024, which would result in empty views if we defaulted to
// the current year. Instead, we intelligently select a year we know has data.
useEffect(() => {
  const savedPositions = localStorage.getItem('taxme_positions');  // Changed!
  const savedTrades = localStorage.getItem('taxme_trades');        // Changed!
  const savedProcessed = localStorage.getItem('processedHoldings');
  
  console.log('Checking for saved data on mount:');
  console.log('- Saved positions:', savedPositions ? 'Yes' : 'No');
  console.log('- Saved trades:', savedTrades ? 'Yes' : 'No');
  console.log('- Saved processed:', savedProcessed ? 'Yes' : 'No');


  // Security Warning
  setIsLoading(true);
  if (!localStorage.getItem('taxme_security_acknowledged')) {
  const userAccepts = window.confirm(
    '📍 How Your Data is Stored\n\n' +
    'This app stores everything locally in THIS browser only:\n\n' +
    '• ✅ Your data never leaves your device (100% private)\n' +
    '• ⚠️ Only accessible from THIS browser on THIS device\n' +
    '• ⚠️ Lost if you clear browser history/cache\n' +
    '• 💾 Storage limit: ~10MB (±5000 trades)\n\n' +
    'Think of it like a notebook that lives in this browser - private but not backed up.\n\n' +
    'ℹ️ This tool is for informational purposes only - not tax advice.\n\n' +
    'Continue?'
  );
    
    if (userAccepts) {
      safeLocalStorageSave('taxme_security_acknowledged', 'true', showNotification);
    }
    else {
      // User declined - clear any existing data and show message
      localStorage.clear();
      setDataLoadStatus('⚠️ App cannot be used without accepting security conditions');
      return; // Don't load any data
    }
  }
  
    if (savedPositions) {
      try {
        const data = JSON.parse(savedPositions);
        const enrichedPositions = enrichPositionsWithBaseCurrency(data.positions || data.data || []);
        setPositionsData(enrichedPositions);
      } catch (e) {
        console.error('Corrupted positions data, clearing:', e);
        localStorage.removeItem('taxme_positions');
      }
    }
  
 if (savedTrades) {
  try {
  const tradesData = JSON.parse(savedTrades);
  const allTrades = tradesData.trades || [];
  const enrichedTrades = enrichTradesWithBaseCurrency(allTrades);
  setTradesData(enrichedTrades);

  // Initialize year selection based on available data
    if (selectedYears.length === 0) {
      const availableYears = getAvailableYearsFromTrades(enrichedTrades);
      if (availableYears.length > 0) {
        setSelectedYears([availableYears[0]]);
        safeLocalStorageSave('taxme_selectedYears', JSON.stringify([availableYears[0]]));
      }
    }
  } catch (e) {
    console.error('Corrupted trades data, clearing:', e);
    localStorage.removeItem('taxme_trades');
  }
}
  
  
  // The existing useEffect will trigger and load processed data
  // once positions and trades are set
// eslint-disable-next-line react-hooks/exhaustive-deps
setIsLoading(false);
// eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

// Calculate taxable gains when data changes (not when tabs change!)
useEffect(() => {
  // Only calculate if we have sell transactions
  if (holdingsData.sellTransactions.length === 0) {
    console.log('No sell transactions yet, skipping tax calc');
    return;
  }
  
  console.log('🧾 Calculating taxes - Data changed');
  console.log('  Sell transactions:', holdingsData.sellTransactions.length);
  console.log('  Positions:', positionsData.length);
  console.log('  Selected years:', selectedYears);
  
  const taxableTransactions = calculateTaxableGains_BE(holdingsData.sellTransactions, positionsData);
  const liabilityData = calculateTaxLiability_BE(taxableTransactions);
  
  setTaxData({
    ...taxData,
    taxableTransactions: taxableTransactions,
    transactionsByYear: liabilityData.transactions,
    summaries: liabilityData.summaries
  });
  
  console.log('✅ Tax calculations complete');
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [holdingsData.sellTransactions.length, positionsData.length, selectedYears]);

  // Apply theme to document
useEffect(() => {
  document.documentElement.setAttribute('data-theme', theme);
  safeLocalStorageSave('taxme_theme', theme);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      // Only auto-switch if user hasn't manually set a preference
      if (!localStorage.getItem('taxme_theme')) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

useEffect(() => {
  if (selectedYears && selectedYears.length > 0) {
    safeLocalStorageSave('taxme_selectedYears', JSON.stringify(selectedYears));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedYears]);


  // ========== AUTO-LOAD SAVED DATA ==========
  // This runs once when the app starts
  useEffect(() => {
  console.log('App starting - checking for saved data...');
  
  // Try to load positions
  const positionsResult = loadPositionsFromStorage();
  
  // Try to load trades
  const tradesResult = loadTradesFromStorage();
  
  // Build status message
  let statusMessage = '';
  
  if (positionsResult.success && tradesResult.success) {
    statusMessage = `✅ Loaded ${positionsResult.count} positions and ${tradesResult.tradeCount} trades from ${tradesResult.monthCount} months`;
  } else if (positionsResult.success) {
    statusMessage = `✅ Loaded ${positionsResult.count} positions from baseline`;
  } else if (tradesResult.success) {
    statusMessage = `✅ Loaded ${tradesResult.tradeCount} trades from ${tradesResult.monthCount} months`;
  }
  
  if (statusMessage) {
    setDataLoadStatus(statusMessage);
  }
  
  // Load saved tab preference
  const savedTab = localStorage.getItem('taxme_activeTab');
  if (savedTab !== null) {
    setActiveTab(parseInt(savedTab));
  }
  
// Load saved tax year - single year only
const savedYears = localStorage.getItem('taxme_selectedYears');
if (savedYears) {
  try {
    const parsed = JSON.parse(savedYears);
    if (Array.isArray(parsed) && parsed.length > 0 && parsed[0] !== 'ALL') {
      setSelectedYears([parsed[0]]); // Ensure single element
      hasAutoSelectedYear.current = true;
    }
  } catch (error) {
    console.error('Error parsing saved years:', error);
    localStorage.removeItem('taxme_selectedYears');
  }
}
// eslint-disable-next-line react-hooks/exhaustive-deps
}, []); // This only runs once on mount


  // Clear saved positions from browser storage
const clearPositionsFromStorage = () => {
  const confirmClear = window.confirm(
    'Are you sure you want to clear saved positions?\n\n' +
    'This will remove the December 31 baseline from browser storage.\n' +
    'You will need to import the CSV again.'
  );
  
  if (!confirmClear) {
    console.log('Clear cancelled by user');
    return;
  }
  
  try {
    localStorage.removeItem('taxme_positions');
    localStorage.removeItem('taxme_positions_checksum');
    localStorage.removeItem('taxme_positions_backup');
    localStorage.removeItem('processedHoldings');
    localStorage.removeItem('taxme_using_sample');
    
    // Reset currency if it was set by positions
    if (currencySource === 'positions') {
      localStorage.removeItem('taxme_userCurrency');
      localStorage.removeItem('taxme_currencySource');
      setUserCurrency('EUR'); // Default
      setCurrencySource(null);
    }
    
    setPositionsData([]);
    setDataLoadStatus('');

    // Auto-expand panel if returning to semi-loaded state
    if (tradesData.length > 0) {
      // Still have trades, so we're in semi-loaded state now
      setControlPanelExpanded(true);
    }
    
    console.log('Positions cleared from storage');
    showNotification('✅ Positions cleared from browser storage', 'success');
    
  } catch (error) {
    console.error('Failed to clear positions:', error);
    showNotification('Failed to clear positions', 'error');
  }
};  

 // Clear saved trades from browser storage
const clearTradesFromStorage = () => {
  const confirmClear = window.confirm(
    'Are you sure you want to clear ALL saved trades?\n\n' +
    'This will remove all trade data from browser storage.\n' +
    'You will need to import the CSV files again.'
  );
  
  if (!confirmClear) {
    console.log('Clear trades cancelled by user');
    return;
  }
  
  try {
    localStorage.removeItem('taxme_trades');
    localStorage.removeItem('taxme_trades_checksums');
    localStorage.removeItem('processedHoldings');
    localStorage.removeItem('taxme_using_sample');
    
    // Only reset currency if it was set by trades (not positions)
    if (currencySource === 'trades') {
      localStorage.removeItem('taxme_userCurrency');
      localStorage.removeItem('taxme_currencySource');
      setUserCurrency('EUR'); // Reset to default
      setCurrencySource(null);
    }
    
    setTradesData([]);

    // Auto-expand panel if returning to semi-loaded state
    if (positionsData.length > 0) {
      // Still have positions, so we're in semi-loaded state now
      setControlPanelExpanded(true);
}

     // Clear tax data
    setTaxData({
      taxableTransactions: [],
      countryCode: 'BE',
      rulesVersion: '2024-2026',
      transactionsByYear: {},
      summaries: {}
    });
    
    // Clear holdings data
    setHoldingsData({
      holdings: [],
      lots: {},
      sellTransactions: [],
      buyTransactions: []
    });

    updateLoadStatus();
    
    console.log('All trades cleared from storage');
    showNotification('✅ All trades cleared from browser storage', 'success');
    
  } catch (error) {
    console.error('Failed to clear trades:', error);
    showNotification('Failed to clear trades', 'error');
  }
};


// Clear ALL saved data from browser storage
const clearAllData = () => {
  const confirmClear = window.confirm(
    '⚠️ WARNING: Clear ALL saved data?\n\n' +
    'This will remove:\n' +
    '• All positions (December 31 baseline)\n' +
    '• All monthly trades\n\n' +
    'You will need to re-import everything.\n\n' +
    'Are you SURE?'
  );

  if (!confirmClear) return;

  // Double-check for safety
  const doubleCheck = window.confirm(
    '🚨 FINAL CONFIRMATION 🚨\n\n' +
    'This action cannot be undone!\n' +
    'Click OK to delete all data.'
  );

  if (!doubleCheck) return;

  try {
    // Clear everything from localStorage
    localStorage.removeItem('taxme_positions');
    localStorage.removeItem('taxme_positions_checksum');
    localStorage.removeItem('taxme_positions_backup');
    localStorage.removeItem('taxme_trades');
    localStorage.removeItem('taxme_trades_checksums');
    localStorage.removeItem('processedHoldings'); 
    localStorage.removeItem('taxme_activeTab');   
    localStorage.removeItem('taxme_selectedYears');
    localStorage.removeItem('taxme_using_sample');
    localStorage.removeItem('taxme_userCurrency');
    localStorage.removeItem('taxme_currencySource');

  

    // Reset all state
    setPositionsData([]);
    setTradesData([]);
    setDataLoadStatus('');
    setUserCurrency('EUR'); 
    setCurrencySource(null);
    setControlPanelExpanded(true);

     // Reset calculated data
    setHoldingsData({
      holdings: [],
      lots: {},
      sellTransactions: [],
      buyTransactions: []
    });

    setTaxData({
      taxableTransactions: [],
      countryCode: 'BE',
      rulesVersion: '2024-2026',
      transactionsByYear: {},
      summaries: {}
    });

    setSelectedYears(null);

    // Reset to overview tab
    setActiveTab(0);

    console.log('All data cleared from storage');
    showNotification('✅ All data cleared from browser storage', 'success');

  } catch (error) {
    console.error('Failed to clear all data:', error);
    showNotification('Failed to clear data', 'error');
  }
};
 
  // ========== FILE HANDLERS ==========

  // Safe localStorage wrapper with quota detection
  const safeLocalStorageSave = (key, value) => {
  try {
    localStorage.setItem(key, value);
    
    // Add timestamp tracking for successful saves
    const now = new Date().toLocaleString();
    localStorage.setItem('taxme_lastSaveTime', now);
    setLastSaveTime(now);
    
    return { success: true };
  } catch (e) {
    // Check if it's a quota exceeded error
    const isQuotaError = e.name === 'QuotaExceededError' || 
                        e.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
                        e.code === 22 || 
                        e.code === 1014;
    
    if (isQuotaError) {
      console.error('❌ localStorage is full!');
      
      // Calculate approximate storage usage
      let totalSize = 0;
      for (let key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          totalSize += localStorage[key].length + key.length;
        }
      }
      const sizeMB = (totalSize / (1024 * 1024)).toFixed(2);
      
      showNotification(
        `⚠️ Browser storage is full (${sizeMB}MB used). Please export your data and clear some space, or use a different browser.`,
        'error'
      );
      
      return { success: false, error: 'QUOTA_EXCEEDED', size: sizeMB };
    } else {
      console.error('❌ localStorage error:', e);
      showNotification('⚠️ Failed to save data to browser storage.', 'error');
      return { success: false, error: 'UNKNOWN', message: e.message };
    }
  }
};

  // Calculate storage usage
const updateStorageInfo = () => {
  let totalSize = 0;
  for (let key in localStorage) {
    if (localStorage.hasOwnProperty(key)) {
      totalSize += (localStorage[key].length + key.length) * 2; // UTF-16
    }
  }
  const sizeMB = totalSize / (1024 * 1024);
  const limit = 10; // Assume 10MB limit
  const percentage = (sizeMB / limit) * 100;
  
  setStorageInfo({ 
    used: sizeMB.toFixed(2), 
    percentage: Math.min(percentage, 100).toFixed(0) 
  });
};

// Update storage info on mount and when data changes
useEffect(() => {
  updateStorageInfo();
}, [positionsData.length, tradesData.length]);


  // Handle when user selects the positions CSV file
const handlePositionsFileSelect = (e) => {
    const confirmed = window.confirm(
   '📅 Positions File Check\n\n' +
  'For accurate tax calculations, please confirm:\n' +
  'Is this file from the last trading day of 2025?\n\n' +
  '✓ Yes - Continue with upload\n' +
  '✗ No - I\'ll get the correct file'
);
  
  if (!confirmed) {
    e.target.value = ''; 
    return; // 
  }

  const file = e.target.files[0];
  if (!file) return;
  
  // Check storage before processing
  if (storageInfo.percentage > 90) {
    const proceed = window.confirm(
      `⚠️ Browser storage is ${storageInfo.percentage}% full!\n\n` +
      `You're using ${storageInfo.used}MB of ~10MB.\n\n` +
      `Continue with import? Consider clearing old data first.`
    );
    if (!proceed) {
      e.target.value = ''; // Reset file input
      return;
    }
  }
  
  console.log("Positions file selected:", file.name);
  processPositionsFileAuto(file);
};

// Validates and imports IB baseline positions file, replacing any existing baseline data
const processPositionsFileAuto = (file) => {
  
  // Step 1: Validate file size
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    showNotification('File too large. Maximum size is 10MB.', 'error');
    return;
  }
  
  const reader = new FileReader();
  
  reader.onload = async (event) => {
    const csvText = event.target.result;
    
    // Step 2: Calculate checksum for duplicate detection
   const calculateChecksum = async (text) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };
    
    const fileChecksum = await calculateChecksum(csvText);
    const lastChecksum = localStorage.getItem('taxme_positions_checksum');
    
    // Step 3: Check if file was already imported
    if (fileChecksum === lastChecksum) {
      showNotification('This file is already imported', 'info');
      return;
    }
    
    setIsLoading(true);
    setLoadingMessage('Processing positions file...');
    
    // Step 4: Parse CSV file
    Papa.parse(csvText, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(),
      complete: (results) => {
        console.log("Parsed data:", results.data);
        
        // Step 5: Validate parsed data exists
        if (!results.data || results.data.length === 0) {
          showNotification('No data found in file', 'error');
          setIsLoading(false);
          return;
        }

        // Step 5.5: Check for duplicate symbols
          const symbols = results.data.map(p => p.Symbol).filter(s => s);
          const duplicates = [...new Set(symbols.filter((s, i) => symbols.indexOf(s) !== i))];
          
          if (duplicates.length > 0) {
            alert(
              `⚠️ Duplicate symbols found: ${duplicates.join(', ')}\n\n` +
              'This tool expects one row per symbol (as provided by Interactive Brokers).\n\n' +
              'If you have multiple accounts:\n' +
              '1. Combine them in Excel first\n' +
              '2. Sum the quantities\n' +
              '3. Calculate weighted average cost\n\n' +
              'Each symbol should appear only once in the final file.'
            );
            setIsLoading(false);
            return;
          }
        
        // Step 6: Define column mappings for flexible header matching
        const columnMappings = {
          symbol: ['Symbol', 'Ticker', 'Security'],
          quantity: ['Quantity', 'Qty', 'Shares', 'Position'],
          markPrice: ['MarkPrice', 'Mark Price', 'Market Price', 'Current Price', 'Price'],
          positionValue: ['PositionValue', 'Position Value', 'Market Value', 'Value'],
          costBasisPrice: ['CostBasisPrice', 'Cost Basis Price', 'Avg Cost', 'Average Cost', 'CostBasis'],
          costBasisMoney: ['CostBasisMoney', 'Cost Basis Money', 'Total Cost', 'Cost'],
          currencyPrimary: ['CurrencyPrimary', 'Currency Primary', 'Currency', 'Curr'],
          fxRateToBase: ['FXRateToBase', 'FX Rate To Base', 'FX Rate', 'Exchange Rate'],
          assetClass: ['AssetClass', 'Asset Class', 'Type', 'Asset Type'],
          reportDate: ['ReportDate', 'Report Date', 'Date', 'As Of Date'],
          multiplier: ['Multiplier', 'ContractMultiplier', 'Contract Multiplier']
        };
        
        // Step 7: Helper function to find actual column names in data
        const findColumn = (headers, possibleNames) => {
          for (const possible of possibleNames) {
            const found = headers.find(h => 
              h.toLowerCase().replace(/[\s_-]/g, '') === 
              possible.toLowerCase().replace(/[\s_-]/g, '')
            );
            if (found) return found;
          }
          return null;
        };
        
        // Step 8: Map required and optional columns
        const headers = Object.keys(results.data[0]);
        
        const requiredMappings = {
          symbol: findColumn(headers, columnMappings.symbol),
          quantity: findColumn(headers, columnMappings.quantity),
          markPrice: findColumn(headers, columnMappings.markPrice),
          positionValue: findColumn(headers, columnMappings.positionValue),
          costBasisPrice: findColumn(headers, columnMappings.costBasisPrice)
        };
        
        const optionalMappings = {
          costBasisMoney: findColumn(headers, columnMappings.costBasisMoney),
          currencyPrimary: findColumn(headers, columnMappings.currencyPrimary),
          fxRateToBase: findColumn(headers, columnMappings.fxRateToBase),
          assetClass: findColumn(headers, columnMappings.assetClass),
          reportDate: findColumn(headers, columnMappings.reportDate),
          multiplier: findColumn(headers, columnMappings.multiplier)
        };
        
        // Step 9: Validate all required columns exist
        const missingColumns = [];
        for (const [field, column] of Object.entries(requiredMappings)) {
          if (!column) {
            missingColumns.push(field);
          }
        }
        
        if (missingColumns.length > 0) {
          showNotification(
            `Missing required columns: ${missingColumns.join(', ')}. ` +
            `This doesn't appear to be an a valid positions export.`,
            'error'
          );
          console.error('Column mappings:', requiredMappings);
          setIsLoading(false);
          return;
        }
        
    // Step 10: Map data to standard format using found column names
        // Truncate strings to prevent memory issues from malformed CSVs
        const truncate = (str, max = 200) => 
          str && str.length > max ? str.substring(0, max) : str;
        
        const mappedData = results.data.map(row => ({
          Symbol: truncate(row[requiredMappings.symbol], 50),
          Quantity: row[requiredMappings.quantity],
          MarkPrice: row[requiredMappings.markPrice],
          PositionValue: row[requiredMappings.positionValue],
          CostBasisPrice: row[requiredMappings.costBasisPrice],
          CostBasisMoney: row[optionalMappings.costBasisMoney] || row[requiredMappings.positionValue],
          CurrencyPrimary: truncate(row[optionalMappings.currencyPrimary], 10) || 'USD',
          FXRateToBase: row[optionalMappings.fxRateToBase] || '1',
          AssetClass: truncate(row[optionalMappings.assetClass], 20) || 'STK',
          ReportDate: truncate(row[optionalMappings.reportDate], 20) || '20251231',
          Multiplier: row[optionalMappings.multiplier] || '1',
          Description: truncate(row.Description, 200),
          SecurityID: truncate(row.SecurityID, 50),
          IssuerCountryCode: truncate(row.IssuerCountryCode, 10),
          SubCategory: truncate(row.SubCategory, 50),
          ...row // Keep all original fields
        }));
        
        // Step 11: Filter and validate positions
        const invalidDatePositions = [];

        const validPositions = mappedData.filter(position => {
          // Skip empty symbols
          if (!position.Symbol || position.Symbol.trim() === '') return false;
          
          // Skip CASH and FOREX positions
          if (position.AssetClass === 'CASH' || position.AssetClass === 'FOREX') {
            console.log(`Skipping ${position.AssetClass} position: ${position.Symbol}`);
            return false;
          }

           // Validate date PROPERLY
            const reportDate = parseIBDate(position.ReportDate);
            if (!reportDate) {
              invalidDatePositions.push({
                symbol: position.Symbol,
                invalidDate: position.ReportDate || 'missing'
              });
              return false; // Skip this position entirely
            }
          
          // Store the validated date back (as ISO string for consistency)
          position.ReportDate = reportDate.toISOString().split('T')[0];
          
          // Validate quantity is positive
          const qty = parseFloat(position.Quantity);
          if (isNaN(qty) || qty <= 0) {
            console.warn(`Invalid quantity for ${position.Symbol}: ${position.Quantity}`);
            return false;
          }

           // Validate date exists
            if (!position.ReportDate || position.ReportDate === null || position.ReportDate === '' || position.ReportDate === 'null') {
              console.warn(`❌ Skipping position with missing date: ${position.Symbol}`);
              return false;
            }
                    
          // Validate prices are not negative
          const markPrice = parseFloat(position.MarkPrice || 0);
          const costPrice = parseFloat(position.CostBasisPrice || 0);
          
          if (markPrice < 0 || costPrice < 0) {
            console.warn(`Negative price for ${position.Symbol}: MarkPrice=${position.MarkPrice}, CostBasis=${position.CostBasisPrice}`);
            return false;
          }
          
          // Ensure at least one valid price exists
          if ((isNaN(markPrice) || markPrice === 0) && (isNaN(costPrice) || costPrice === 0)) {
            console.warn(`No valid price for ${position.Symbol}: MarkPrice=${position.MarkPrice}, CostBasis=${position.CostBasisPrice}`);
            return false;
          }
          
          return true;
        });
        
        // Step 12: Validate we have positions after filtering
        if (validPositions.length === 0) {
          showNotification('❌ No valid positions found - nothing was saved', 'error');
          setIsLoading(false);
          return;
        }

        // Show warning if any positions were skipped due to invalid dates
        if (invalidDatePositions.length > 0) {
                    
          showNotification(
          `⚠️ Skipped ${invalidDatePositions.length} positions (invalid dates) - saved the rest to browser storage`,
            'warning'
          );
          
          console.error('Positions skipped due to invalid dates:', invalidDatePositions);
        }

        // Step 12.5: Detect base currency from positions
          const basePosition = validPositions.find(p => 
            parseFloat(p.FXRateToBase || p[optionalMappings.fxRateToBase] || 1) === 1
          );

          if (basePosition) {
            const detectedCurrency = basePosition.CurrencyPrimary || basePosition[optionalMappings.currencyPrimary] || 'EUR';
            const previousCurrency = userCurrency;
            const previousSource = currencySource;
            
            // Update currency
            setUserCurrency(detectedCurrency);
            setCurrencySource('positions');
            localStorage.setItem('taxme_userCurrency', detectedCurrency);
            localStorage.setItem('taxme_currencySource', 'positions');
            
            console.log('Currency detected from positions:', detectedCurrency);
            
            // Show notification only if overriding trades-detected currency
            if (previousSource === 'trades' && previousCurrency !== detectedCurrency) {
              showNotification(
                `Currency updated to ${detectedCurrency} based on positions file (was ${previousCurrency} from trades)`,
                'info'
              );
            }
          } else {
            console.log('No base currency detected in positions (no FXRate = 1)');
          }
        
        // Step 13: Handle replacement of existing data
        if (positionsData.length > 0) {
          const action = window.confirm(
            `⚠️ Replace Existing Baseline?\n\n` +
            `Current: ${positionsData.length} positions\n` +
            `New file: ${validPositions.length} positions\n\n` +
            `This will REPLACE all existing baseline data.\n\n` +
            `Continue?`
          );
          
          if (!action) {
            setIsLoading(false);
            return;
          }
          
          // Create backup before replacement
          const backup = {
            date: new Date().toISOString(),
            data: positionsData,
            checksum: lastChecksum
          };
          safeLocalStorageSave('taxme_positions_backup', JSON.stringify(backup), showNotification);
        }
        
        // Step 14: Enrich positions with base currency calculations
        console.log(`Processing ${validPositions.length} valid positions`);
        const enrichedPositions = enrichPositionsWithBaseCurrency(validPositions);
        
        // Step 15: Update React state
        setPositionsData(enrichedPositions);
        
        // Step 16: Save to localStorage
        const storageData = {
          importDate: new Date().toISOString(),
          fileName: file.name,
          rowCount: enrichedPositions.length,
          data: enrichedPositions
        };

      const saveResult = safeLocalStorageSave('taxme_positions', JSON.stringify(storageData), showNotification);
      if (!saveResult.success) {
        setIsLoading(false);
        return; // Stop here - don't continue
      }

safeLocalStorageSave('taxme_positions_checksum', fileChecksum, showNotification);
        
        // Step 17: Show success message
        showNotification(
          `✅ ${validPositions.length} positions saved to browser storage` +
          (mappedData.length > validPositions.length 
            ? ` (${mappedData.length - validPositions.length} invalid rows skipped)` 
            : ''),
          'success'
        );
        
        setIsLoading(false);
      },
      error: (error) => {
        console.error("Error parsing CSV:", error);
        showNotification('Error reading CSV file. Please check the format.', 'error');
        setIsLoading(false);
      }
    });
  };
  
  reader.onerror = () => {
    showNotification('Failed to read file. Please try again.', 'error');
    setIsLoading(false);
  };
  
  reader.readAsText(file);
};

const handleTradesFileSelect = (e) => {
  const files = Array.from(e.target.files);
  if (files.length === 0) return;
  
  // Check storage before processing
  if (storageInfo.percentage > 90) {
    const proceed = window.confirm(
      `⚠️ Browser storage is ${storageInfo.percentage}% full!\n\n` +
      `You're using ${storageInfo.used}MB of ~10MB.\n\n` +
      `You selected ${files.length} file${files.length > 1 ? 's' : ''} to import.\n` +
      `Consider clearing old data first.\n\n` +
      `Continue with import?`
    );
    if (!proceed) {
      e.target.value = ''; // Reset file input
      return;
    }
  }
  
  // Check file limit
  if (files.length > 10) {
    showNotification('Maximum 10 files can be imported at once. Please select fewer files.', 'error');
    e.target.value = ''; // Reset the input
    return;
  }
  
  console.log(`${files.length} trade files selected`);
  
  // Process files sequentially with Promise-based approach
  processTradesSequentially(files);
};

  
const processTradesSequentially = async (files) => {
  setIsLoading(true);
  setLoadingMessage(`Processing ${files.length} trade file${files.length > 1 ? 's' : ''}...`);
  
  let successCount = 0;
  let skipCount = 0;
  const errors = [];
  const duplicates = [];
  
  for (let i = 0; i < files.length; i++) {
    setLoadingMessage(`Processing file ${i + 1} of ${files.length}: ${files[i].name}`);
    
    try {
      const result = await processTradesFileAsync(files[i]);
      
      if (result.status === 'success') {
        successCount++;
      } else if (result.status === 'duplicate') {
        skipCount++;
        duplicates.push(files[i].name);
      } else if (result.status === 'error') {
        skipCount++;
        errors.push(`${files[i].name}: ${result.message}`);
      }
      
    } catch (error) {
      console.error(`Failed to process ${files[i].name}:`, error);
      errors.push(`${files[i].name}: ${error.message}`);
      skipCount++;
    }
    
    // Small delay between files to let UI breathe
    if (i < files.length - 1) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }
  }
  
  setIsLoading(false);
  
 // Build summary notification
let message = `✅ ${successCount} trade file${successCount !== 1 ? 's' : ''} saved to browser storage`;

  if (skipCount > 0) {
    message += `, skipped ${skipCount} file${skipCount !== 1 ? 's' : ''}`;
  }

  if (duplicates.length > 0) {
    message += ` (${duplicates.length} duplicate${duplicates.length > 1 ? 's' : ''})`;
  }

  if (errors.length > 0) {
    message += ` (${errors.length} failed)`;
    console.error('Import errors:', errors);
  }

  showNotification(message, errors.length > 0 ? 'warning' : 'success');
};


// Shared year selector component used by multiple tabs
const YearSelector = ({ selectedYear, onYearChange, availableYears }) => {
  return (
    <select
      value={selectedYear || ''}
      onChange={(e) => onYearChange(e.target.value)}
      style={{
        marginRight: '10px',
        padding: '4px 8px',
        borderRadius: '4px',
        border: '1px solid var(--color-border)',
        backgroundColor: 'var(--color-background)',
        color: 'var(--color-text)',
        cursor: 'pointer',
        fontSize: '0.9em'
      }}
    >
      <option value="" disabled>Select Year</option>
      {availableYears.map(year => (
        <option key={year} value={year}>{year}</option>
      ))}
    </select>
  );
};

// Generic empty state component for tabs requiring both positions and trades
const EmptyState = ({ icon = "📁", message = "To explore your tax implications, ensure both your baseline positions and trade history are uploaded." }) => (
  <div className="empty-state">
    <div className="empty-state-icon">{icon}</div>
    <h4 className="empty-state-title">No Data Available</h4>
    <p className="empty-state-message">{message}</p>
  </div>
);


// Enriches positions array with base currency calculations and multiplier handling
const enrichPositionsWithBaseCurrency = (positions) => {
  return positions.map(position => {
    // Use multiplier from IB data, default to 1 if missing
    const multiplier = parseFloat(position.Multiplier || 1);
    
    // Apply multiplier to quantity only
    const notionalQuantity = parseFloat(position.Quantity || 0) * multiplier;
    
    // DON'T multiply money values - they're already totals
    const totalPositionValue = parseFloat(position.PositionValue || 0);
    const totalCostBasis = parseFloat(position.CostBasisMoney || 0);
    
    return {
      ...position,
      // Keep original contract quantity for reference
      ContractQuantity: position.Quantity,
      // Use notional quantity for all calculations
      Quantity: notionalQuantity.toString(),
      // Money values stay as-is
      PositionValue: totalPositionValue.toString(),
      CostBasisMoney: totalCostBasis.toString(),
      CostBasisPrice: position.CostBasisPrice,
      // Calculate base currency
      CostBasisMoneyBase: convertToBaseCurrency(totalCostBasis, position.FXRateToBase),
      PositionValueBase: convertToBaseCurrency(totalPositionValue, position.FXRateToBase),
      // Store multiplier for reference
      Multiplier: multiplier
    };
  });
};

// Helper function to convert notional quantities back to contract/unit counts for display
// Handles all instruments with multipliers (options, futures, CFDs, forex lots, etc.)
const getDisplayQuantity = (item) => {
  // Get the quantity value (could be in different fields)
  let quantity = item.Quantity || item.currentQuantity || item.quantity || 0;
  quantity = parseFloat(quantity);
  
  // Get the multiplier (could be in different fields or not exist)
  const multiplier = parseFloat(item.Multiplier || item.multiplier || 1);
  
  // If we have ContractQuantity, prefer that (most accurate)
  if (item.ContractQuantity) {
    return item.ContractQuantity;
  }
  
  // If multiplier exists and isn't 1, divide to get contracts
  if (multiplier !== 1) {
    return Math.round(quantity / multiplier);
  }
  
  // Otherwise return quantity as-is
  return quantity;
};

// Enriches trades array with base currency calculations and multiplier handling
const enrichTradesWithBaseCurrency = (trades) => {
  return trades.map(trade => {
    // Use multiplier from IB data, default to 1 if missing
    const multiplier = parseFloat(trade.Multiplier || 1);
    
    // Apply multiplier to quantity only
    const notionalQuantity = parseFloat(trade.Quantity || 0) * multiplier;
    
    // DON'T multiply money values - they're already totals
    const totalTradeMoney = parseFloat(trade.TradeMoney || 0);
    
    return {
      ...trade,
      // Keep original contract quantity for reference
      ContractQuantity: trade.Quantity,
      // Use notional quantity for all calculations
      Quantity: notionalQuantity.toString(),
      // Money values stay as-is
      TradeMoney: totalTradeMoney.toString(),
      TradeMoneyBase: convertToBaseCurrency(totalTradeMoney, trade.FXRateToBase),
      // Store multiplier for reference
      Multiplier: multiplier
    };
  });
};

// Formats numbers as currency with proper separators and currency symbol
// Input: amount (number), currency code (optional, defaults to detected currency)
// Output: Formatted string like "€1,234.56" or "$1,234.56"
const formatCurrency = (amount, currency = null) => {
  // Use provided currency or fall back to detected user currency
  const curr = currency || userCurrency;
  
  // Define currency symbols
  const symbols = {
    'EUR': '€',
    'USD': '$',
    'GBP': '£',
    'CHF': 'CHF ',
    'CAD': 'C$',
    'AUD': 'A$'
  };
  
  // Get symbol or use currency code if not defined
  const symbol = symbols[curr] || curr + ' ';
  
  // Format with thousand separators and 2 decimal places
  const formatted = Math.abs(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  // Add symbol and handle negative numbers
  return amount < 0 ? `-${symbol}${formatted}` : `${symbol}${formatted}`;
};

// Converts any amount to base currency using the FX rate
// Input: amount (number or string), fxRate (number or string)
// Output: converted amount (number)
const convertToBaseCurrency = (amount, fxRate) => {
  return parseFloat(amount || 0) * parseFloat(fxRate || 1);
};

// Holding tab summary card calculations
const calculateHoldingsMetrics = (holdings, positions, baseCurrency) => {
  // Input validation
  if (!holdings || holdings.length === 0) {
    return {
      totalCost: 0,
      activeCount: 0,
      baseCurrencyPercentage: 0,
      assetClassCount: 0
    };
  }

  const activeHoldings = holdings.filter(h => h.status === 'ACTIVE');
  
  // Calculate total cost basis
  const totalCost = activeHoldings.reduce((sum, holding) => {
    return sum + (holding.totalCostBase || 
      (holding.currentQuantity * holding.avgCost * holding.fxRate));
  }, 0);

  // Calculate base currency percentage
  const baseCurrencyCost = activeHoldings
    .filter(h => h.fxRate === 1)
    .reduce((sum, holding) => {
      return sum + (holding.totalCostBase || 
        (holding.currentQuantity * holding.avgCost));
    }, 0);
  
  const baseCurrencyPercentage = totalCost > 0 
    ? Math.round((baseCurrencyCost / totalCost) * 100)
    : 0;

  // Count unique asset classes
  const assetClasses = new Set(
    activeHoldings
      .map(h => h.assetClass || 'STK')
      .filter(ac => ac !== null && ac !== undefined)
  );
  const assetClassCount = assetClasses.size;

  return {
    totalCost,
    activeCount: activeHoldings.length,
    baseCurrencyPercentage,
    assetClassCount
  };
};

const calculateTradingMetrics = (sellTransactions, buyTransactions) => {
  if (!sellTransactions || sellTransactions.length === 0) {
    return {
      totalRealizedPL: 0,
      totalRealizedPLPercent: 0,
      winCount: 0,
      lossCount: 0,
      winRate: 0,
      avgHoldingDays: 0,
      avgWinAmount: 0,
      avgLossAmount: 0,
      transactions: []
    };
  }

  // Enhance transactions with P&L calculations
  const enhancedTransactions = sellTransactions.map(transaction => {
    // Calculate gain/loss in trade currency
    const realGainLoss = (transaction.salePrice - transaction.purchasePrice) * transaction.quantity;
    
    // Calculate gain/loss in base currency
    const totalCostBase = transaction.quantity * 
      (transaction.purchasePriceBase || transaction.purchasePrice * transaction.fxRatePurchase);
    const totalProceedsBase = transaction.quantity * 
      (transaction.salePriceBase || transaction.salePrice * transaction.fxRateSale);
    const realGainLossBase = totalProceedsBase - totalCostBase;
    
    // Calculate percentage return
    const realGainLossPercent = ((transaction.salePrice - transaction.purchasePrice) / 
      transaction.purchasePrice) * 100;
    
    // Calculate holding period
    const saleDate = parseIBDate(transaction.tradeDate);
    const purchaseDate = parseIBDate(transaction.purchaseDate);
    const holdingPeriodDays = Math.floor((saleDate - purchaseDate) / 86400000);
    
    // Calculate additional metrics
    const totalCost = transaction.quantity * transaction.purchasePrice;
    const totalProceeds = transaction.quantity * transaction.salePrice;
    const gainLossPerShare = transaction.salePrice - transaction.purchasePrice;
    const gainLossPerShareBase = (transaction.salePriceBase || transaction.salePrice * transaction.fxRateSale) -
      (transaction.purchasePriceBase || transaction.purchasePrice * transaction.fxRatePurchase);
    
    return {
      ...transaction,
      realGainLoss,
      realGainLossPercent,
      realGainLossBase,
      holdingPeriodDays,
      totalCost,
      totalCostBase,
      totalProceeds,
      totalProceedsBase,
      gainLossPerShare,
      gainLossPerShareBase
    };
  });

  // Calculate aggregate metrics
  const wins = enhancedTransactions.filter(t => t.realGainLossBase > 0);
  const losses = enhancedTransactions.filter(t => t.realGainLossBase <= 0);
  
  const totalRealizedPL = enhancedTransactions.reduce((sum, t) => sum + t.realGainLossBase, 0);
  const totalCost = enhancedTransactions.reduce((sum, t) => sum + t.totalCostBase, 0);
  const avgHoldingDays = enhancedTransactions.reduce((sum, t) => sum + t.holdingPeriodDays, 0) / 
    (enhancedTransactions.length || 1);

  return {
    // Aggregate metrics
    totalRealizedPL,
    totalRealizedPLPercent: totalCost > 0 ? (totalRealizedPL / totalCost) * 100 : 0,
    winCount: wins.length,
    lossCount: losses.length,
    winRate: enhancedTransactions.length > 0 ? 
      (wins.length / enhancedTransactions.length) * 100 : 0,
    avgHoldingDays: Math.round(avgHoldingDays),
    avgWinAmount: wins.length > 0 ? 
      wins.reduce((sum, t) => sum + t.realGainLossBase, 0) / wins.length : 0,
    avgLossAmount: losses.length > 0 ? 
      losses.reduce((sum, t) => sum + t.realGainLossBase, 0) / losses.length : 0,
    
    // Enhanced transactions
    transactions: enhancedTransactions
  };
};

// Helper to update the load status banner
const updateLoadStatus = () => {
  const positionsResult = loadPositionsFromStorage();
  const tradesResult = loadTradesFromStorage();
  
  let statusMessage = '';
  
  if (positionsResult.success && tradesResult.success) {
    statusMessage = `✅ Loaded ${positionsResult.count} positions and ${tradesResult.tradeCount} trades`;
  } else if (positionsResult.success) {
    statusMessage = `✅ Loaded ${positionsResult.count} positions from baseline`;
  } else if (tradesResult.success) {
    statusMessage = `✅ Loaded ${tradesResult.tradeCount} trades`;
  }
  
  if (statusMessage) {
    setDataLoadStatus(statusMessage);
  }
};

// Helper to show notifications
const showNotification = (message, type = 'success') => {
  // Check if this exact message is already showing
  setNotifications(prev => {
    const alreadyShowing = prev.some(n => n.message === message);
    
    if (alreadyShowing) {
      console.log('Notification already showing:', message);
      return prev; // Return unchanged array
    }
    
    const newNotification = {
    id: crypto.randomUUID(),
      message: message,
      type: type
    };
    
    return [...prev, newNotification];
  });
};

// Helper to show sort arrow for a column
const getSortArrow = (column) => {
  if (sortColumn !== column) return '';
  return sortDirection === 'asc' ? ' ▲' : ' ▼';
};

// Function to sort holdings array
const sortHoldings = (holdings) => {
  return [...holdings].sort((a, b) => {
    let aValue = a[sortColumn];
    let bValue = b[sortColumn];
    
    // Handle string comparison (symbol, status)
    if (typeof aValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    // Handle number comparison (quantities, costs)
    return sortDirection === 'asc'
      ? aValue - bValue
      : bValue - aValue;
  });
};

// Function to sort positions array (for Baseline tab)
const sortPositions = (positions) => {
  return [...positions].sort((a, b) => {
    let aValue = a[sortColumn];
    let bValue = b[sortColumn];
    
    // Convert to numbers for numeric columns
    if (['Quantity', 'MarkPrice', 'PositionValue', 'CostBasisPrice', 'CostBasisMoney', 'UnrealizedPL'].includes(sortColumn)) {
      aValue = parseFloat(aValue) || 0;
      bValue = parseFloat(bValue) || 0;
    }
    
    // String comparison
    if (typeof aValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    // Number comparison
    return sortDirection === 'asc'
      ? aValue - bValue
      : bValue - aValue;
  });
};

// Function to sort trades array
const sortTrades = (trades) => {
  return [...trades].sort((a, b) => {
    let aValue = a[sortColumn];
    let bValue = b[sortColumn];
    
    // Convert to numbers for numeric columns
    if (['Quantity', 'TradePrice', 'TradeMoney'].includes(sortColumn)) {
      aValue = Math.abs(parseFloat(aValue)) || 0;
      bValue = Math.abs(parseFloat(bValue)) || 0;
    }
    
    // Special handling for dates
    if (sortColumn === 'TradeDate') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }
    
    // String comparison
    if (typeof aValue === 'string' && sortColumn !== 'TradeDate') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    // Number/Date comparison
    return sortDirection === 'asc' 
      ? aValue - bValue
      : bValue - aValue;
  });
};

// Theme toggle handler function
const toggleTheme = () => {
  setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
};

  // Load positions data from browser's local storage
  const loadPositionsFromStorage = () => {
    try {
      // Try to get saved data
      const savedData = localStorage.getItem('taxme_positions');
      
      // Check if we found anything
      if (savedData) {
        // Parse the JSON string back to an object
        const storageData = JSON.parse(savedData);
        
        // Extract the positions array
        const positions = storageData.data;
        
        // Update our state with saved positions
        setPositionsData(positions);
        
        // Let user know we loaded their data
        console.log(`Loaded ${positions.length} positions from ${storageData.importDate}`);
        
        // Format the date nicely
        const importDate = new Date(storageData.importDate);
        const dateString = importDate.toLocaleDateString('en-GB');
        
        // Set status message
        setDataLoadStatus(`✅ Loaded ${positions.length} positions from ${dateString}`);
        
        // Return true to indicate success
        return {
          success: true,
          count: positions.length,
          date: storageData.importDate
        };
      }
      
      // No saved data found
      return false;
      
    } catch (error) {
      console.error('Failed to load positions:', error);
      return { success: false };
    }
  };

// Load trades data from browser's local storage
const loadTradesFromStorage = () => {
  try {
    // Try to get saved trades data
    const savedData = localStorage.getItem('taxme_trades');
    
    // Check if we found anything
    if (savedData) {
      // Parse the JSON string back to an object
      const tradesData = JSON.parse(savedData);
      
      // Extract trades array
      const allTrades = tradesData.trades || [];
      
      // Update our state with all trades
      setTradesData(allTrades);
      
      // Log summary
      console.log(`Loaded ${allTrades.length} trades from storage`);
      console.log(`Last import: ${tradesData.lastImport}`);
      console.log(`Total files imported: ${tradesData.totalFiles}`);
      
      // Return info for status display
      return {
        success: true,
        tradeCount: allTrades.length,
        lastImport: tradesData.lastImport,
        totalFiles: tradesData.totalFiles
      };
    }
    
    // No saved data found
    return { success: false };
    
  } catch (error) {
    console.error('Failed to load trades:', error);
    return { success: false, error: error.message };
  }
};

// Generate a unique ID for trades missing TradeID
const generateUniqueTradeID = (trade) => {
  // Generate random suffix to ensure uniqueness
  const randomSuffix = Math.random().toString(36).substring(2, 11); // 9 chars
  
  const generatedID = `GEN-${randomSuffix}`;
  
  console.log(`Generated TradeID for ${trade.Symbol}: ${generatedID}`);
  
  return generatedID;
};


// Validates and imports trades CSV, merging with existing trades and handling duplicates
const processTradesFileAsync = (file) => {
  return new Promise((resolve) => {
  
      // Step 1: Validate file size
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
      resolve({ status: 'error', message: 'File too large (max 10MB)' });
      return;
}
      
      const reader = new FileReader();
      
      reader.onload = async (event) => {
        const csvText = event.target.result;
        
        // Step 2: Calculate checksum for duplicate detection
        const calculateChecksum = async (text) => {
        const encoder = new TextEncoder();
        const data = encoder.encode(text);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      };
        
        const fileChecksum = await calculateChecksum(csvText);
        const importedChecksums = JSON.parse(localStorage.getItem('taxme_trades_checksums') || '[]');
        
        // Step 3: Check if file was already imported
        if (importedChecksums.includes(fileChecksum)) {
          resolve({ status: 'duplicate', message: 'Already imported' });
          return;
        }
        
        setIsLoading(true);
        setLoadingMessage('Processing trades file...');
        
        // Step 4: Parse CSV file
        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (header) => header.trim(),
          complete: (results) => {
            console.log("Parsed trade data:", results.data);
            
            // Step 5: Validate parsed data exists
           if (!results.data || results.data.length === 0) {
            resolve({ status: 'error', message: 'No data found in file' });
            return;
          }
            
            // Step 6: Define column mappings for flexible header matching
            const columnMappings = {
              tradeID: ['TradeID', 'Trade ID', 'ID', 'Order ID', 'OrderID'],
              symbol: ['Symbol', 'Ticker', 'Security'],
              tradeDate: ['TradeDate', 'Trade Date', 'Date', 'Transaction Date'],
              buySell: ['Buy/Sell', 'BuySell', 'Side', 'Action'],
              quantity: ['Quantity', 'Qty', 'Shares', 'Amount'],
              tradePrice: ['TradePrice', 'Trade Price', 'Price', 'Execution Price'],
              tradeMoney: ['TradeMoney', 'Trade Money', 'Total', 'Net Amount'],
              currencyPrimary: ['CurrencyPrimary', 'Currency Primary', 'Currency'],
              fxRateToBase: ['FXRateToBase', 'FX Rate To Base', 'FX Rate', 'Exchange Rate'],
              assetClass: ['AssetClass', 'Asset Class', 'Type'],
              subCategory: ['SubCategory', 'Sub Category', 'SubCat'],
              multiplier: ['Multiplier', 'ContractMultiplier', 'Contract Multiplier']
            };
            
            // Step 7: Helper function to find actual column names in data
            const findColumn = (headers, possibleNames) => {
              for (const possible of possibleNames) {
                const found = headers.find(h => 
                  h.toLowerCase().replace(/[\s_-]/g, '') === 
                  possible.toLowerCase().replace(/[\s_-]/g, '')
                );
                if (found) return found;
              }
              return null;
            };
            
            // Step 8: Map required and optional columns
            const headers = Object.keys(results.data[0]);
            
            const requiredMappings = {
              tradeID: findColumn(headers, columnMappings.tradeID),
              symbol: findColumn(headers, columnMappings.symbol),
              tradeDate: findColumn(headers, columnMappings.tradeDate),
              buySell: findColumn(headers, columnMappings.buySell),
              quantity: findColumn(headers, columnMappings.quantity),
              tradePrice: findColumn(headers, columnMappings.tradePrice),
              tradeMoney: findColumn(headers, columnMappings.tradeMoney)
            };
            
            const optionalMappings = {
              currencyPrimary: findColumn(headers, columnMappings.currencyPrimary),
              fxRateToBase: findColumn(headers, columnMappings.fxRateToBase),
              assetClass: findColumn(headers, columnMappings.assetClass),
              subCategory: findColumn(headers, columnMappings.subCategory),
              multiplier: findColumn(headers, columnMappings.multiplier)
            };
            
            // Step 9: Validate all required columns exist
            const missingColumns = [];
            for (const [field, column] of Object.entries(requiredMappings)) {
              if (!column) {
                missingColumns.push(field);
              }
            }
            
          if (missingColumns.length > 0) {
            resolve({ 
              status: 'error', 
              message: `Missing columns: ${missingColumns.join(', ')}` 
            });
            return;
          }
                      
            // Step 10: Group rows by Symbol + TradeDate + Buy/Sell for IB multi-row format
            const groupedRows = new Map();
            
            results.data.forEach(row => {
              // Skip CASH and FOREX using mapped columns
              if (optionalMappings.assetClass && 
                  (row[optionalMappings.assetClass] === 'CASH' || 
                  row[optionalMappings.assetClass] === 'FOREX')) {
                return;
              }
              
              // Group using mapped column names
              if (row[requiredMappings.symbol] && row[requiredMappings.tradeDate]) {
                const buySell = row[requiredMappings.buySell] || '';
                const key = `${row[requiredMappings.symbol]}_${row[requiredMappings.tradeDate]}_${buySell}`;
                
                if (!groupedRows.has(key)) {
                  groupedRows.set(key, []);
                }
                groupedRows.get(key).push(row);
              }
            });
            
            console.log(`Grouped ${results.data.length} rows into ${groupedRows.size} groups`);
            
            // Step 11: Process groups to find execution rows and average FX rates
            const processedTrades = [];
            let skippedGroups = 0;
            
            groupedRows.forEach((rowsInGroup, groupKey) => {
              // Find execution row using mapped column
              const executionRow = rowsInGroup.find(row => 
                row[requiredMappings.buySell] === 'BUY' || 
                row[requiredMappings.buySell] === 'SELL'
              );
              
              if (!executionRow) {
                console.log(`No execution row found for group: ${groupKey}`);
                skippedGroups++;
                return;
              }
              
              // Validate using mapped columns
              if (!executionRow[requiredMappings.tradePrice] || 
                  !executionRow[requiredMappings.tradeMoney] || 
                  !executionRow[requiredMappings.quantity]) {
                console.warn(`Execution row missing required fields for group: ${groupKey}`);
                skippedGroups++;
                return;
              }
              
              // Average FX rates if present
              const fxRates = rowsInGroup
                .map(row => parseFloat(row[optionalMappings.fxRateToBase] || 1))
                .filter(rate => !isNaN(rate) && rate > 0);
              
              const avgFxRate = fxRates.length > 0 
                ? Math.round((fxRates.reduce((sum, rate) => sum + rate, 0) / fxRates.length) * 1000000) / 1000000
                : 1.0;
              
              // Create processed trade with mapped columns
              const processedTrade = {
                ...executionRow,
                [optionalMappings.fxRateToBase || 'FXRateToBase']: avgFxRate.toString(),
                [requiredMappings.quantity]: Math.abs(parseFloat(executionRow[requiredMappings.quantity] || 0)).toString()
              };
              
              processedTrades.push(processedTrade);
            });
            
            console.log(`Processed ${processedTrades.length} trades from ${groupedRows.size} groups`);
            if (skippedGroups > 0) {
              console.warn(`Warning: Skipped ${skippedGroups} groups with no execution row`);
            }
            
            // Step 12: Validate we have trades after processing
            if (processedTrades.length === 0) {
              showNotification('No valid trades found after processing IB multi-row format', 'error');
              setIsLoading(false);
              return;
            }

            // Step 12.5: Detect base currency from trades (only if not set by positions)
            if (currencySource !== 'positions') {
              const baseTrade = processedTrades.find(t => 
                parseFloat(t[optionalMappings.fxRateToBase || 'FXRateToBase'] || 1) === 1
              );
              
              if (baseTrade) {
                const detectedCurrency = baseTrade[optionalMappings.currencyPrimary] || 'EUR';
                
                // Only set if not already set or if previously set by trades
                if (!currencySource || currencySource === 'trades') {
                  setUserCurrency(detectedCurrency);
                  setCurrencySource('trades');
                  localStorage.setItem('taxme_userCurrency', detectedCurrency);
                  localStorage.setItem('taxme_currencySource', 'trades');
                  
                  console.log('Currency detected from trades:', detectedCurrency);
                }
              }
            }
            
          // Step 13: Map trades to standard format
            // Truncate strings to prevent memory issues from malformed CSVs
            const truncate = (str, max = 200) => 
              str && str.length > max ? str.substring(0, max) : str;
            
            const invalidDateTrades = [];

            const mappedTrades = processedTrades
              .map(row => ({
                TradeID: truncate(row[requiredMappings.tradeID], 50),
                Symbol: truncate(row[requiredMappings.symbol], 50),
                TradeDate: truncate(row[requiredMappings.tradeDate], 20),
                'Buy/Sell': truncate(row[requiredMappings.buySell], 10),
                Quantity: row[requiredMappings.quantity],
                TradePrice: row[requiredMappings.tradePrice],
                TradeMoney: row[requiredMappings.tradeMoney],
                CurrencyPrimary: truncate(row[optionalMappings.currencyPrimary], 10) || 'USD',
                FXRateToBase: row[optionalMappings.fxRateToBase] || '1',
                AssetClass: truncate(row[optionalMappings.assetClass], 20) || 'STK',
                SubCategory: truncate(row[optionalMappings.subCategory], 50) || '',
                Multiplier: row[optionalMappings.multiplier] || '1',
                SourceFile: truncate(file.name, 100),
                ImportDate: new Date().toISOString(),
                Description: truncate(row.Description, 200),
                IssuerCountryCode: truncate(row.IssuerCountryCode, 10),
                ...row // Keep original fields
              }))
                // Generate TradeID if missing
                .map(trade => {
                  if (!trade.TradeID || trade.TradeID === '' || trade.TradeID === 'undefined' || trade.TradeID === 'null') {
                    trade.TradeID = generateUniqueTradeID(trade);
                  }
                  return trade;
                })  
              .filter(trade => {
                
                // Step 14: Filter out invalid trades
                if (!trade.Symbol || trade.Symbol.trim() === '') return false;
                if (trade.AssetClass === 'CASH' || trade.AssetClass === 'FOREX') return false;
                
                   // Validate date PROPERLY
                    const tradeDate = parseIBDate(trade.TradeDate);
                    if (!tradeDate) {
                      invalidDateTrades.push({
                        symbol: trade.Symbol,
                        tradeId: trade.TradeID,
                        invalidDate: trade.TradeDate || 'missing'
                      });
                      return false;
                    }
                    // Store validated date back
                    trade.TradeDate = tradeDate.toISOString().split('T')[0];
  
                const qty = parseFloat(trade.Quantity);
                if (isNaN(qty) || qty === 0) {
                  console.warn(`Invalid quantity for ${trade.Symbol}: ${trade.Quantity}`);
                  return false;
                }
                
                if (!trade['Buy/Sell'] || trade['Buy/Sell'].trim() === '') {
                  console.warn(`Missing Buy/Sell for ${trade.Symbol}`);
                  return false;
                }
                
                return true;
              });
            
            if (mappedTrades.length === 0) {
              showNotification('No valid trades found in file', 'error');
              setIsLoading(false);
              return;
            }

            // Show warning if any trades were skipped due to invalid dates
              if (invalidDateTrades.length > 0) {
                               
                showNotification(
                `⚠️ Skipped ${invalidDateTrades.length} invalid trades - saved the rest to browser storage`,
                  'warning'
                );
                
                console.error('Trades skipped due to invalid dates:', invalidDateTrades);
              }

              console.log('Required mappings found:', requiredMappings);
              console.log('First row after mapping:', mappedTrades[0]);
            
            
          // Step 15: Merge with existing trades
              const existingData = localStorage.getItem('taxme_trades');
              const existingTrades = existingData ? JSON.parse(existingData).trades || [] : [];

              const tradeMap = new Map();
              existingTrades.forEach(trade => {
                // Use TradeID as primary key (strict deduplication)
                const key = trade.TradeID;
                tradeMap.set(key, trade);
              });

              let updatedCount = 0;
              let newCount = 0;
              mappedTrades.forEach(trade => {
                // Use TradeID as primary key (strict deduplication)
                const key = trade.TradeID;
                if (tradeMap.has(key)) {
                  console.log(`Updating existing trade: ${key}`);
                  updatedCount++;
                } else {
                  console.log(`Adding new trade: ${key}`);
                  newCount++;
                }
                tradeMap.set(key, trade);
              });
            
            // Step 16: Sort and enrich trades
            const allTrades = Array.from(tradeMap.values())
              .sort((a, b) => new Date(a.TradeDate) - new Date(b.TradeDate));
            
            const enrichedTrades = enrichTradesWithBaseCurrency(allTrades);
            
            // Step 17: Save to localStorage
            const storageData = {
              trades: enrichedTrades,
              lastImport: new Date().toISOString(),
              totalFiles: (existingData ? JSON.parse(existingData).totalFiles || 0 : 0) + 1
            };
           const saveResult = safeLocalStorageSave('taxme_trades', JSON.stringify(storageData));
            if (!saveResult.success) {
              setIsLoading(false);
              return; // Don't continue if save failed
            }

            importedChecksums.push(fileChecksum);
            safeLocalStorageSave('taxme_trades_checksums', JSON.stringify(importedChecksums));
            
            // Step 18: Update React state and clear cache
            setTradesData(enrichedTrades);
            localStorage.removeItem('processedHoldings');
            setHoldingsData({
              holdings: [],
              lots: {},
              sellTransactions: [],
              buyTransactions: []
            });
            
            // Step 19: Show success message
            resolve({ 
            status: 'success', 
            newCount: newCount,
            updatedCount: updatedCount 
          });
          },
         error: (error) => {
          resolve({ status: 'error', message: error.message });
        }
        });
      };
      
     reader.onerror = () => {
      resolve({ status: 'error', message: 'Failed to read file' });
    };
          
      reader.readAsText(file);
      }); 
    };


  // Create calculateCurrentHoldings
  const calculateCurrentHoldings = (positions, trades) => {
  // Step 1: Initialize lots and sell transactions
  const lots = {};  // Empty object to store lots by symbol
  const sellTransactions = [];
  const buyTransactions = [];
  const lotCounters = {};  // Track lot sequence numbers
  const errors = [];  // Track calculation errors

  console.log('Starting with empty lots object');

  // Step 2: Loop through each position
  positions.forEach(position => {
    const symbol = position.Symbol;
    
  // Skip empty symbols
    if (!symbol || symbol.trim() === '') return;

  // Step 3: Create array for this symbol if it doesn't exist
    if (!lots[symbol]) {
      lots[symbol] = [];  // Empty array to hold lots for this symbol
  }
  
  // Step 4: Create a lot from this position
 const lot = {

    // Add lot ID for baseline positions
  lotId: `${symbol}-2025-12-31-BASELINE`,

  // Basic info
  symbol: symbol,  // Good to have it here too
  isin: position.SecurityID,  // Unique identifier
  description: position.Description,  // Company name
  
  // Quantities
  quantity: parseFloat(position.Quantity),
  remainingQuantity: parseFloat(position.Quantity),
  
  // Prices
  purchasePrice: parseFloat(position.CostBasisPrice),
  totalCost: parseFloat(position.CostBasisMoney),
  dec31Price: parseFloat(position.MarkPrice),  // Per share
  dec31Value: parseFloat(position.PositionValue),  // Total
  
  // Currency info
  currency: position.CurrencyPrimary,
  fxRate: parseFloat(position.FXRateToBase || 1),  // Default to 1 if missing
  
  // Dates
  purchaseDate: position.ReportDate,
  acquisitionDate:'2025-12-31',
  
  // Asset info
  assetClass: position.AssetClass,
  country: position.IssuerCountryCode,

  // Base Currency Fields
  purchasePriceBase: parseFloat(position.CostBasisPrice) * parseFloat(position.FXRateToBase || 1),
  totalCostBase: parseFloat(position.CostBasisMoney) * parseFloat(position.FXRateToBase || 1),
  dec31PriceBase: parseFloat(position.MarkPrice) * parseFloat(position.FXRateToBase || 1),
  dec31ValueBase: parseFloat(position.PositionValue) * parseFloat(position.FXRateToBase || 1)
  };

  console.log(`Created baseline lot: ${lot.lotId} for ${symbol}`);

// Step 5: Add this lot to the symbol's array
  lots[symbol].push(lot);

    
 // Step 6: Return the lots (not empty array!)
  console.log('Created lots:', lots);
  });

 // Step 7: Sort trades by date (oldest first)
// Filter out trades with missing dates first
const validTrades = trades.filter(t => {
  if (!t.TradeDate || t.TradeDate === null || t.TradeDate === '') {
    console.warn('Skipping trade with missing date:', t.TradeID || t.Symbol);
    return false;
  }
  return true;
});

const sortedTrades = [...validTrades].sort((a, b) => {
  return new Date(a.TradeDate) - new Date(b.TradeDate);
});
console.log('Sorted trades:', sortedTrades.length);

// Step 8: Process each trade in chronological order
  sortedTrades.forEach(trade => {
  const symbol = trade.Symbol;
  const action = trade['Buy/Sell'];
  
  console.log(`Processing ${action} trade for ${symbol}`);
  
  // Step 9: Different logic for BUY vs SELL  
    if (action === 'BUY') {
  // First, check if symbol exists in lots
  if (!lots[symbol]) {
    lots[symbol] = [];  // Create array if new symbol
  }

  // Set up lot counter for this symbol-date
  const counterKey = `${symbol}-${trade.TradeDate}`;
  if (!lotCounters[counterKey]) {
    lotCounters[counterKey] = 0;  // Need this for unique IDs!
  }
  
  // Increment counter and generate lot ID
  lotCounters[counterKey]++;
  const sequence = lotCounters[counterKey].toString().padStart(3, '0'); // 1 → "001"
  const lotId = `${symbol}-${trade.TradeDate}-${sequence}`;

  // Create NEW lot from this buy trade
  const newLot = {
  lotId: lotId,
  quantity: parseFloat(trade.Quantity),
  remainingQuantity: parseFloat(trade.Quantity),
  purchasePrice: parseFloat(trade.TradePrice),
  purchaseDate: trade.TradeDate,
  acquisitionDate: trade.TradeDate,
  totalCost: parseFloat(trade.TradeMoney),
  currency: trade.CurrencyPrimary,
  assetClass: trade.AssetClass,
  subCategory: trade.SubCategory,  
  country: trade.IssuerCountryCode, 
  description: trade.Description || symbol,
  fxRate: parseFloat(trade.FXRateToBase || 1),
  purchasePriceBase: parseFloat(trade.TradePrice) * parseFloat(trade.FXRateToBase || 1),
  totalCostBase: parseFloat(trade.TradeMoney) * parseFloat(trade.FXRateToBase || 1)
  };

    // ADD THIS: Capture buy transaction
  const buyTransaction = {
    tradeDate: trade.TradeDate,
    symbol: trade.Symbol,
    quantity: parseFloat(trade.Quantity),
    price: parseFloat(trade.TradePrice),
    totalValue: parseFloat(trade.TradeMoney),
    currency: trade.CurrencyPrimary,
    fxRate: parseFloat(trade.FXRateToBase || 1),
    lotId: lotId  // Which lot this trade created
  };

  buyTransactions.push(buyTransaction);

  console.log(`Created lot ${lotId} for ${symbol}`);
  
// Step 10: Add this lot to the symbol's array
  lots[symbol].push(newLot);
  
  console.log(`Added new lot for ${symbol}: ${newLot.quantity} shares @ ${newLot.purchasePrice}`);
}



// ==================== SELL TRADE PROCESSING ====================
// When processing a SELL trade, we need to:
// 1. Reduce quantities from existing lots (FIFO - oldest first)
// 2. Track which specific lots we're selling from
// 3. Capture tax event data for each lot reduction
// 
// Tax Event Tracking (Added for Phase 3 - Tax Engine):
// - We record the "facts" of each sale (prices, dates, quantities)
// - We do NOT calculate gains here (that's done in Tax Tab)
// - This separation keeps our code modular and tax rules flexible
// ==============================================================
  
 else if (action === 'SELL') {
  const quantityToSell = Math.abs(parseFloat(trade.Quantity));
  let remainingToSell = quantityToSell;
  
  console.log(`=== Processing SELL trade ===`);
  console.log(`Symbol: ${symbol}`);
  console.log(`Quantity to sell: ${quantityToSell}`);
  console.log(`Available lots:`, lots[symbol]);
  console.log(`Need to sell ${quantityToSell} shares of ${symbol}`);

  // Step 11: Check if we have lots for this symbol
  if (!lots[symbol] || !Array.isArray(lots[symbol])) {
    console.error(`ERROR: Trying to sell ${symbol} but no lots found!`);
    return;  // Skip this trade
  }
  
  // Step 12: Go through lots in order (FIFO)
  for (const lot of lots[symbol]) {
    if (remainingToSell === 0) break;  // All sold, stop!
    

    console.log('Lot being processed:', lot);
    console.log('Lot ID:', lot.lotId);

    if (lot.remainingQuantity === 0) continue;  // Skip empty lots
    
    // Step 13: Calculate how many shares to take from this lot
    const sharesToTake = Math.min(remainingToSell, lot.remainingQuantity);
    
    console.log(`Taking ${sharesToTake} shares from lot with ${lot.remainingQuantity} available`);

    
      // Step 14: Update the quantities
      lot.remainingQuantity -= sharesToTake;  
      remainingToSell -= sharesToTake;        

      console.log(`Lot now has ${lot.remainingQuantity} shares remaining`);
      console.log(`Still need to sell ${remainingToSell} shares`);

    // ========== CAPTURE COMPLETE SELL TRANSACTION ==========
// Record full details of this partial sale from a specific lot
// This is raw transaction data - tax calculations come later
// ======================================================

const sellTransaction = {
  // When and what
  tradeDate: trade.TradeDate,
  symbol: trade.Symbol,
  quantity: sharesToTake,

  // Which lot was this from
  lotId: lot.lotId, 
  
  // Original purchase details (from this lot)
  purchaseDate: lot.purchaseDate,
  acquisitionDate: lot.acquisitionDate,
  purchasePrice: lot.purchasePrice,
  purchasePriceBase: lot.purchasePriceBase || (lot.purchasePrice * lot.fxRate),
  
  // Sale details (from current trade)
  salePrice: parseFloat(trade.TradePrice),
  salePriceBase: parseFloat(trade.TradePrice) * parseFloat(trade.FXRateToBase || 1),

  // Multiplier Field
  multiplier: lot.multiplier || 
             positionsData.find(p => p.Symbol === trade.Symbol)?.Multiplier || 
             trade.Multiplier || 
             '1',
  
  // Additional context
  currency: trade.CurrencyPrimary,
  tradeMoney: parseFloat(trade.TradeMoney),
  tradeMoneyBase: parseFloat(trade.TradeMoney) * parseFloat(trade.FXRateToBase || 1),
  fxRatePurchase: lot.fxRate || 1,
  fxRateSale: parseFloat(trade.FXRateToBase || 1)
};

// Add to our transactions array
sellTransactions.push(sellTransaction);


        }

// Step 15: Check if we tried to sell more than we own
if (remainingToSell > 0) {
      const shortfall = remainingToSell;
      const attempted = quantityToSell;
      const available = quantityToSell - remainingToSell;
      
      console.error(`❌ OVERSELLING DETECTED: ${symbol}`);
      console.error(`   Trade Date: ${trade.TradeDate}`);
      console.error(`   Attempted to sell: ${attempted}`);
      console.error(`   Available shares: ${available}`);
      console.error(`   Shortfall: ${shortfall}`);
      
      errors.push({
        type: 'OVERSELLING',
        symbol: symbol,
        tradeDate: trade.TradeDate,
        attempted: attempted,
        available: available,
        shortfall: shortfall
      });
    }
}
});

// Step 16: Aggregate lots into holdings array
  const holdings = [];

  // Loop through each symbol in lots
  Object.entries(lots).forEach(([symbol, symbolLots]) => {
    console.log(`Aggregating ${symbol} with ${symbolLots.length} lots`);
   
  // Step 17: Initialize counters for this symbol
  let totalQuantity = 0;
  let totalCost = 0;
  let totalCostBase = 0;

  // Step 18: Sum up all lots for this symbol
  symbolLots.forEach(lot => {
    totalQuantity += lot.remainingQuantity;
    totalCost += (lot.remainingQuantity * lot.purchasePrice);
    totalCostBase += (lot.remainingQuantity * (lot.purchasePriceBase || lot.purchasePrice * lot.fxRate));
  });

  console.log(`${symbol}: Total ${totalQuantity} shares, Total cost €${totalCost}`);

  // Calculate original totals for base currency
  const originalTotalCostBase = symbolLots.reduce((sum, lot) => 
    sum + (lot.quantity * (lot.purchasePriceBase || lot.purchasePrice * lot.fxRate)), 0);

    // Step 19: Create holding object for this symbol
    const baselinePosition = positionsData.find(position => position.Symbol === symbol);
    const baselineQty = baselinePosition ? parseFloat(baselinePosition.Quantity) : 0;

     // Calculate average costs
      const avgCostTrade = totalQuantity > 0 ? totalCost / totalQuantity : 0;
      const avgCostBase = totalQuantity > 0 ? totalCostBase / totalQuantity : 0;

      // Track last activity date and price
      let lastActivityDate = '2025-12-31'; // Default to baseline
      let lastKnownPrice = parseFloat(baselinePosition?.MarkPrice || 0);
      let lastKnownPriceBase = parseFloat(baselinePosition?.MarkPrice || 0) * parseFloat(baselinePosition?.FXRateToBase || 1);

      // Find the most recent trade for this symbol
      const symbolBuys = buyTransactions.filter(tx => tx.symbol === symbol);
      const symbolSells = sellTransactions.filter(tx => tx.symbol === symbol);
      const allSymbolTrades = [...symbolBuys, ...symbolSells]
        .sort((a, b) => new Date(b.tradeDate) - new Date(a.tradeDate));

      if (allSymbolTrades.length > 0) {
        const lastTrade = allSymbolTrades[0];
        lastActivityDate = lastTrade.tradeDate;
        
        // Get price from last trade
        const lastSell = symbolSells.find(tx => tx.tradeDate === lastTrade.tradeDate);
        if (lastSell) {
          lastKnownPrice = lastSell.salePrice || 0;
          lastKnownPriceBase = lastSell.salePriceBase || (lastSell.salePrice * lastSell.fxRateSale);
        } else {
          const lastBuy = symbolBuys.find(tx => tx.tradeDate === lastTrade.tradeDate);
          if (lastBuy) {
            lastKnownPrice = lastBuy.price || 0;
            lastKnownPriceBase = (lastBuy.price || 0) * (lastBuy.fxRate || 1); 
            }
        }
      }

      // Pre-calculate all values
      const lastKnownValue = totalQuantity * lastKnownPrice;
      const lastKnownValueBase = totalQuantity * lastKnownPriceBase;
      const unrealizedPL = lastKnownValue - totalCost;
      const unrealizedPLBase = lastKnownValueBase - totalCostBase;
      const unrealizedReturn = totalCost > 0 ? (unrealizedPL / totalCost) * 100 : 0;
      const unrealizedReturnBase = totalCostBase > 0 ? (unrealizedPLBase / totalCostBase) * 100 : 0;

      const holding = {
      symbol: symbol,
      currentQuantity: totalQuantity,
      avgCost: avgCostTrade,
      avgCostBase: avgCostBase,
      status: totalQuantity === 0 ? 'CLOSED' : 'ACTIVE',

    lastActivityDate: lastActivityDate,
    lastKnownPrice: lastKnownPrice,
    lastKnownPriceBase: lastKnownPriceBase,
    lastKnownValue: lastKnownValue,
    lastKnownValueBase: lastKnownValueBase,
    unrealizedPL: unrealizedPL,
    unrealizedPLBase: unrealizedPLBase,  
    unrealizedReturn: unrealizedReturn,
    unrealizedReturnBase: unrealizedReturnBase,


      originalQuantity: symbolLots.reduce((sum, lot) => sum + lot.quantity, 0),
      totalLots: symbolLots.length,
      originalAvgCost: symbolLots.reduce((sum, lot) => sum + (lot.quantity * lot.purchasePrice), 0) /
                      symbolLots.reduce((sum, lot) => sum + lot.quantity, 0) || 0,
      baselineQty: baselineQty,  
      qtyChange: totalQuantity - baselineQty, 

      // Get multiplier from first lot or position
      multiplier: symbolLots[0]?.multiplier || 
             positionsData.find(p => p.Symbol === symbol)?.Multiplier || 
             '1',

      // ADD BASE CURRENCY FIELDS
      totalCostBase: totalCostBase,
      originalAvgCostBase: symbolLots.reduce((sum, lot) => sum + lot.quantity, 0) > 0 ? 
        originalTotalCostBase / symbolLots.reduce((sum, lot) => sum + lot.quantity, 0) : 0,
      
      // Get currency from first lot (all lots for same symbol should have same currency)
      currency: symbolLots[0]?.currency || 'USD',
      fxRate: symbolLots[0]?.fxRate || 1,
      assetClass: symbolLots[0]?.assetClass,  
      subCategory: symbolLots[0]?.subCategory,  
      country: symbolLots[0]?.country
    };
      
  

// Step 20: Add this holding to our array
holdings.push(holding);
});

// Step 21: Return results including any errors found
return {
  holdings, 
  sellTransactions, 
  lots, 
  buyTransactions,
  errors
};
};

// Month names array for date formatting
const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Formats IB date strings into readable display format
// Input: "20250722" (YYYYMMDD format from Interactive Brokers)
// Output: "22-Jul-25" (DD-MMM-YY format for international clarity)
// Uses parseIBDate to handle the conversion, then formats for display
const formatDateForDisplay = (dateString) => {
  if (!dateString || dateString === '' || dateString === 'null' || dateString === 'undefined') {
    return '-';  // Keep it simple
  }
  
  try {
    const dateObj = parseIBDate(dateString);
    if (!dateObj) {
      return 'Invalid Date';  // This should rarely happen now
    }
    
    const d = dateObj.getDate();
    const m = dateObj.getMonth();
    const y = dateObj.getFullYear();
    
    return `${d}-${monthNames[m]}-${y.toString().slice(-2)}`;
  } catch (error) {
    console.error('Error formatting date:', dateString, error);
    return 'Error';
  }
};

// Load sample data for demo purposes
const loadSampleData = () => {
  // Clear any existing data first
  localStorage.removeItem('taxme_positions');
  localStorage.removeItem('taxme_trades');
  localStorage.removeItem('processedHoldings');
 
  // Enrich and set sample positions
  const enrichedPositions = enrichPositionsWithBaseCurrency(SAMPLE_DATA.positions);
  setPositionsData(enrichedPositions);
  
  // Save positions to localStorage
  const positionsStorage = {
    importDate: new Date().toISOString(),
    fileName: "sample_positions.csv",
    rowCount: enrichedPositions.length,
    data: enrichedPositions
  };
  safeLocalStorageSave('taxme_positions', JSON.stringify(positionsStorage), showNotification);

   // Filter out trades with invalid dates BEFORE processing
  const validSampleTrades = SAMPLE_DATA.trades.filter(trade => {
    const dateObj = parseIBDate(trade.TradeDate);
    if (!dateObj) {
      console.warn(`Sample data: Skipping trade with invalid date - ${trade.Symbol}, Date: "${trade.TradeDate}"`);
      return false;
    }
    // Update the trade with validated date
    trade.TradeDate = dateObj.toISOString().split('T')[0];
    return true;
  });

   // Generate TradeIDs for sample trades if missing
  const tradesWithIDs = validSampleTrades.map(trade => {
    if (!trade.TradeID || trade.TradeID === null || trade.TradeID === '' || trade.TradeID === 'undefined') {
      return {
        ...trade,
        TradeID: generateUniqueTradeID(trade)
      };
    }
    return trade;
  });

  
// Enrich and set sample trades (use tradesWithIDs instead of SAMPLE_DATA.trades)
  const enrichedTrades = enrichTradesWithBaseCurrency(tradesWithIDs);
  setTradesData(enrichedTrades);
  
  // Save trades to localStorage
  const tradesStorage = {
    trades: enrichedTrades,
    lastImport: new Date().toISOString(),
    totalFiles: 1
  };
  safeLocalStorageSave('taxme_trades', JSON.stringify(tradesStorage), showNotification)
  
  // Set flag to indicate sample data is loaded
  safeLocalStorageSave('taxme_using_sample', 'true', showNotification);

   // Set currency for sample data
  setUserCurrency('EUR'); // Sample data is in USD
  setCurrencySource('positions');
  localStorage.setItem('taxme_userCurrency', 'EUR');
  localStorage.setItem('taxme_currencySource', 'positions');
  
  // Clear holdings cache to force recalculation
  setHoldingsData({
    holdings: [],
    lots: {},
    sellTransactions: [],
    buyTransactions: []
  });


   // Show notification about filtered trades
  const skippedCount = SAMPLE_DATA.trades.length - validSampleTrades.length;
  if (skippedCount > 0) {
    showNotification(
  `🎯 Sample data saved to browser storage (${skippedCount} invalid trades skipped)`,
      'warning'
    );
  } else {
    showNotification('🎯 Sample data saved to browser storage', 'success');
  }
};


const calculateTaxableGains_BE = (sellTransactions, positionsData) => {
  return sellTransactions.map(transaction => {
    // Find matching position for baseline price
    const matchingPosition = positionsData.find(position => position.Symbol === transaction.symbol);
    
    // Get baseline prices in both currencies
    const baselinePrice = matchingPosition 
      ? parseFloat(matchingPosition.MarkPrice) 
      : null;
    const baselinePriceBase = matchingPosition 
      ? parseFloat(matchingPosition.MarkPrice) * parseFloat(matchingPosition.FXRateToBase || 1)
      : null;
    
    // Determine if this is a pre-2026 position
    const isPreTaxRegime = transaction.lotId.includes('BASELINE');

    // Handle post-2026 positions (no baseline) before attempting baseline calculations:
    if (!isPreTaxRegime) {
      // Post-2026 position - simple calculation
      const salePriceBase = transaction.salePriceBase || (transaction.salePrice * transaction.fxRateSale);
      const purchasePriceBase = transaction.purchasePriceBase || (transaction.purchasePrice * transaction.fxRatePurchase);
      
      return {
        ...transaction,
        baselinePrice: null,
        baselinePriceBase: null,
        costBasisUsed: transaction.purchasePrice,
        costBasisUsedBase: purchasePriceBase,
        taxableGainLoss: (salePriceBase - purchasePriceBase) * transaction.quantity,
        taxableGainLossTrade: (transaction.salePrice - transaction.purchasePrice) * transaction.quantity,
        realGainLoss: transaction.realGainLossBase || transaction.realGainLoss,
        taxRuleStatus: 'POST-26',
        taxRuleTooltip: 'Acquired after 2026 - standard capital gains rules'
      };
    }

  // Check if sale is after 2030
    let saleYear;
    try {
      saleYear = parseIBDate(transaction.tradeDate).getFullYear();
    } catch (error) {
      console.warn('Could not parse sale date:', transaction.tradeDate);
      saleYear = new Date().getFullYear(); // Default to current year
    }
    const isPostTransition = saleYear > TRANSITION_END_YEAR;
    
    // Determine cost basis using asymmetric rules
    let costBasisBase;
    let costBasisTrade;

    if (isPreTaxRegime && baselinePriceBase !== null) {
      const salePriceBase = transaction.salePriceBase || (transaction.salePrice * transaction.fxRateSale);
      const purchasePriceBase = transaction.purchasePriceBase || (transaction.purchasePrice * transaction.fxRatePurchase);
      
      // Check if this is a loss (sale price < purchase price)
      const isLoss = salePriceBase < purchasePriceBase;
      
      if (isPostTransition) {
        // POST-2030: Must use baseline for EVERYTHING (gains and losses)
        costBasisBase = baselinePriceBase;
        costBasisTrade = baselinePrice;
      } else if (isLoss) {
        // PRE-2031 LOSS: Must use baseline price
          costBasisBase = baselinePriceBase;
          costBasisTrade = baselinePrice;
      } else {
        // PRE-2031 GAIN: Apply MAX rule (can use purchase price if higher)
    costBasisBase = Math.max(purchasePriceBase, baselinePriceBase);
    costBasisTrade = Math.max(transaction.purchasePrice, baselinePrice);
      }
    }
    
    // Calculate taxable gain/loss IN BASE CURRENCY (EUR)
    const salePriceBase = transaction.salePriceBase || (transaction.salePrice * transaction.fxRateSale);
    const purchasePriceBase = transaction.purchasePriceBase || (transaction.purchasePrice * transaction.fxRatePurchase);
    const taxableGainLossBase = (salePriceBase - costBasisBase) * transaction.quantity;
    // Also keep trade currency calculation for display purposes
    const taxableGainLossTrade = (transaction.salePrice - costBasisTrade) * transaction.quantity;

    // Determine tax rule status
    let taxRuleStatus = 'POST-26';

    if (isPreTaxRegime && baselinePriceBase !== null) {
      const isLoss = salePriceBase < purchasePriceBase;
      
      if (isPostTransition) {
        // Post-2030: MAX rule expired - baseline forced for ALL pre-2026 positions
        taxRuleStatus = 'EXPIRED';
      } else if (isLoss) {
        // Pre-2031 loss: forced to use baseline
        taxRuleStatus = 'FORCED';
      } else {
        // Pre-2031 gain: check if MAX rule was applied
        if (costBasisBase === purchasePriceBase && purchasePriceBase > baselinePriceBase) {
          taxRuleStatus = 'MAX';
        } else {
          taxRuleStatus = 'PROTECTED';
        }
      }
    }

    // Tooltip text
    let tooltipText = '';
    switch(taxRuleStatus) {
      case 'MAX':
        tooltipText = 'Purchase price higher than baseline - taxpayer benefit applied';
        break;
      case 'FORCED':
        tooltipText = 'Pre-2026 loss - Belgian law requires baseline usage';
        break;
      case 'PROTECTED':
        tooltipText = 'Pre-2026 appreciation excluded from taxation';
        break;
      case 'EXPIRED':
        tooltipText = 'Sold after 2030 - purchase price option no longer available';
        break;
      case 'POST-26':
        tooltipText = 'Acquired after 2026 - standard capital gains rules';
        break;
      default:
        tooltipText = '';
    }
    
    // Return enhanced transaction with tax information
    return {
      ...transaction,
      baselinePrice: baselinePrice,
      baselinePriceBase: baselinePriceBase,
      costBasisUsed: costBasisTrade,  // Trade currency for display
      costBasisUsedBase: costBasisBase,  // Base currency for tax
      taxableGainLoss: taxableGainLossBase,  // IN EUR for tax calculations!
      taxableGainLossTrade: taxableGainLossTrade,  // In trade currency for reference
      realGainLoss: transaction.realGainLossBase || transaction.realGainLoss,  // Use base if available
      taxRuleStatus: taxRuleStatus, 
      taxRuleTooltip: tooltipText
    };
  });
};

const calculateTaxLiability_BE = (taxableTransactions) => {
  console.log('calculateTaxLiability_BE input:', taxableTransactions);
  // Group transactions by year
  const transactionsByYear = {};
  const summaries = {};
  
  taxableTransactions.forEach(transaction => {
    // Skip transactions with missing dates
    if (!transaction.tradeDate || transaction.tradeDate === null || transaction.tradeDate === '') {
      console.warn('Skipping transaction with missing date:', transaction.symbol);
      return; // Skip this transaction
    }
    
    try {
      const dateObj = parseIBDate(transaction.tradeDate);
      console.log('Parsing date:', transaction.tradeDate, '-> Year:', dateObj.getFullYear());
      const year = dateObj.getFullYear().toString();
      
      transactionsByYear[year] ??= [];
      transactionsByYear[year].push(transaction);
    } catch (error) {
      console.error('Error parsing transaction date:', transaction.tradeDate, error);
      return; // Skip this transaction if date parsing fails
    }
  }); // ← End of forEach
  
  console.log('Transactions by year:', transactionsByYear);
  console.log('Years found:', Object.keys(transactionsByYear));
  
  // Process each year
  Object.keys(transactionsByYear).sort().forEach(year => {
    const { exemption, rate } = getTaxConfig(parseInt(year));
    // Sort by date - also needs fixing
    transactionsByYear[year].sort((a, b) => {
      return parseIBDate(a.tradeDate) - parseIBDate(b.tradeDate);
    });
    
    // Add running net
    let runningNet = 0;
    for (let i = 0; i < transactionsByYear[year].length; i++) {
      runningNet += transactionsByYear[year][i].taxableGainLoss;
      transactionsByYear[year][i].runningNet = runningNet;
    }
    
    // Calculate summary
    let totalGains = 0;
    let totalLosses = 0;
    let totalRealizedGains = 0;
    
    for (let i = 0; i < transactionsByYear[year].length; i++) {
      const gainLoss = transactionsByYear[year][i].taxableGainLoss;
      const realGainLoss = transactionsByYear[year][i].realGainLoss || 0; 

      totalRealizedGains += realGainLoss;


      if (gainLoss > 0) {
        totalGains += gainLoss;
      } else {
        totalLosses += gainLoss;
      }
    }
    
    const net = totalGains + totalLosses;
    let taxable = 0;
    let exemptionUsed = 0;
    
   if (net <= exemption) {
    taxable = 0;
    exemptionUsed = net > 0 ? net : 0;
  } else {
    taxable = net - exemption;
    exemptionUsed = exemption;
  }

  const taxDue = taxable > 0 ? taxable * rate : 0;

  summaries[year] = {
    gains: totalGains,
    losses: totalLosses,
    net: net,
    totalRealizedGains: totalRealizedGains,
    exemptionUsed: exemptionUsed,
    exemptionLimit: exemption,
    taxRate: rate,
    exemptionPercentage: Math.round((exemptionUsed / exemption) * 100), 
    taxable: taxable,
    taxDue: taxDue
  };

  }); 
  
  return {
    transactions: transactionsByYear,
    summaries: summaries
  };
}; 

// Helper function to get lot info for a trade
const getLotInfoForTrade = (trade) => {
  if (trade['Buy/Sell'] === 'BUY') {
    // Find matching buy transaction
    const buyTx = holdingsData.buyTransactions.find(
      tx => tx.tradeDate === trade.TradeDate && 
            tx.symbol === trade.Symbol &&
            tx.quantity === Math.abs(parseFloat(trade.Quantity))
    );
    return buyTx ? { type: 'created', lotId: buyTx.lotId } : null;
  } else {
    // Find matching sell transactions (might be multiple lots)
    const sellTxs = holdingsData.sellTransactions.filter(
      tx => tx.tradeDate === trade.TradeDate && 
            tx.symbol === trade.Symbol
    );

    // Handle orphaned sells explicitly
        if (sellTxs.length === 0) {
          // No matching sell transactions found - this is an orphaned sell
          return { 
            type: 'orphaned',
            message: '⚠️ No position found - cannot calculate P&L',
            tooltip: 'This sell has no corresponding buy trades or baseline position. Import missing purchase history to see complete P&L.'
          };
        }

    return sellTxs.length > 0 ? { type: 'sold', transactions: sellTxs } : null;
  }
};

// Notification Component
const Notification = ({ notification, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(notification.id);
    }, 3000);
    
    return () => clearTimeout(timer);
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - we want this to run once on mount
  
  return (
    <div className={`notification notification-${notification.type}`}>          
      <span style={{ flex: 1 }}>{notification.message}</span>
      <button
        onClick={() => onClose(notification.id)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          opacity: 0.6,
          fontSize: '1.2em'
        }}
      >
        ×
      </button>
    </div>
  );
};


// Check localStorage availability (must be after all hooks)
  if (!localStorageAvailable) {
    return (
      <div style={{
        fontFamily: 'var(--font-family)',
        maxWidth: '600px',
        margin: '100px auto',
        padding: '20px',
        textAlign: 'center'
      }}>
        <h1 style={{ color: 'var(--color-danger)' }}>Storage Not Available</h1>
        <p>Browser storage is required for TaxMe to save your data.</p>
        <p>Please check that you're not in Private/Incognito mode and that cookies are enabled.</p>
      </div>
    );
  }

// Loading Spinner Component
const LoadingSpinner = ({ message = 'Processing...' }) => {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'var(--color-background-overlay)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '4px solid var(--color-border)',
        borderTopColor: 'var(--color-primary)',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />
      <p style={{
        marginTop: 'var(--space-3)',
        color: 'var(--color-text-secondary)',
        fontSize: '1.1em'
      }}>
        {message}
      </p>
    </div>
  );
};

// Define tab configuration
const tabs = [
  { id: 0, name: 'Tax', icon: '🧾' },
  { id: 1, name: 'Holdings', icon: '📈' },
  { id: 2, name: 'Trades', icon: '💰' },
  { id: 3, name: 'Baseline', icon: '📅' }
];

// Clear notifications when switching views
const handleTabChange = (tabId) => {
  setActiveTab(tabId);
  localStorage.setItem('taxme_activeTab', tabId);
  setNotifications([]); // Clear notifications when switching tabs
};

// Handler for column header clicks
const handleSort = (column) => {
  if (sortColumn === column) {
    // Same column clicked - reverse direction
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
  } else {
    // New column clicked - set it and default to ascending
    setSortColumn(column);
    setSortDirection('asc');
  }
};

// ========== RENDER UI ==========
return (
  <div className="App">
      {/* Top Bar */}
      <div className="top-bar">
        <div className="top-bar-content">
          {/* Mobile: Hamburger Menu */}
          <button 
            className="hamburger-button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menu"
          >
            <span className="hamburger-icon">☰</span>
          </button>

          {/* App Title */}
          <div className="app-header-text">
            <h1 className="app-title">
              TaxMe <span className="app-subtitle-inline">Belgian Capital Gains Tracker</span>
            </h1>
          </div>

          {/* Desktop: Auth and Theme Controls */}
          <div className="top-bar-right-desktop">
            {/* Show Sign In button when logged out */}
            <SignedOut>
              <SignInButton mode="modal">
                <button className="sign-in-btn" title="Sign in to access premium features"> 
                  Sign In
                </button>
              </SignInButton>
            </SignedOut>
            
            {/* Show User menu when logged in */}
            <SignedIn>
              <UserButton afterSignOutUrl="/" />
            </SignedIn>

            {/* Theme toggle */}  
            <button 
              className="theme-toggle"
              onClick={toggleTheme}
              aria-label="Toggle theme"
              title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            >
              <MoonIcon />
              <SunIcon />
            </button>

            {/* Beta Badge */}
            <span className="beta-badge" title="This app is in beta - we're actively improving it">
              BETA
            </span>
            {/* Storage Indicator */}
          <span 
            className={`storage-badge ${
              storageInfo.percentage > 90 ? 'danger' : 
              storageInfo.percentage > 70 ? 'warning' : 
              'safe'
            }`}
            data-tooltip={`📊 Browser Storage Status

            Currently using: ${storageInfo.used}MB of ~10MB (${storageInfo.percentage}%)
            Last saved: ${lastSaveTime || 'No data saved yet'}
            
            ⚠️ Remember: Data is stored in THIS browser only (no cloud backup)

            🟢 Green (0-70%) = Plenty of space
            🟡 Yellow (70-90%) = Consider clearing old data
            🔴 Red (90-100%) = Storage nearly full

            💡 Tip: Export regularly for backup, clear old data to free space`}
          >
            💾 {storageInfo.used}MB
          </span>
          </div>
          {/* Mobile: Only Theme Toggle */}
          <div className="top-bar-right-mobile">
            <button 
              className="theme-toggle"
              onClick={toggleTheme}
              aria-label="Toggle theme"
              title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            >
              <MoonIcon />
              <SunIcon />
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay and Panel */}
        {mobileMenuOpen && (
          <>
            <div 
              className="mobile-menu-overlay"
              onClick={() => setMobileMenuOpen(false)}
            />
            <div className="mobile-menu">
              <div className="mobile-menu-header">
                <h2>Menu</h2>
                <button 
                  className="mobile-menu-close"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label="Close menu"
                >
                  ✕
                </button>
              </div>
              
              <div className="mobile-menu-content">
                {/* Sign In / User Profile */}
                <div className="mobile-menu-item">
                  <SignedOut>
                    <SignInButton mode="modal">
                      <button 
                        className="mobile-menu-button"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        🔐 Sign In
                      </button>
                    </SignInButton>
                  </SignedOut>
                  <SignedIn>
                    <div className="mobile-menu-user">
                      <UserButton 
                        afterSignOutUrl="/"
                        appearance={{
                          elements: {
                            avatarBox: "w-10 h-10"
                          }
                        }}
                      />
                      <span>Your Account</span>
                    </div>
                  </SignedIn>
                </div>

                {/* Beta Badge Info */}
                <div className="mobile-menu-item mobile-menu-info">
                  <span className="beta-badge-large">BETA</span>
                  <span className="mobile-menu-text">Early access version - we're actively improving it</span>
                </div>
                   {/* Storage Info */}
                  <div className="mobile-menu-item mobile-menu-info">
                    <span 
                      className={`storage-badge ${
                        storageInfo.percentage > 90 ? 'danger' : 
                        storageInfo.percentage > 70 ? 'warning' : 
                        'safe'
                      }`}
                      style={{ fontSize: '13px', padding: '6px 10px' }}
                    >
                      💾 Storage: {storageInfo.used}MB
                    </span>
                    <span className="mobile-menu-text">
                      {storageInfo.percentage}% of ~10MB browser storage used
                    </span>
                  </div>       
                {/* Divider */}
                <div className="mobile-menu-divider" />
                
                {/* Navigation Links */}
                <button 
                  className="mobile-menu-button"
                  onClick={() => {
                    setCurrentView('help');
                    setMobileMenuOpen(false);
                  }}
                >
                  ❓ Help
                </button>
                
                <button 
                  className="mobile-menu-button"
                  onClick={() => {
                    setCurrentView('terms');
                    setMobileMenuOpen(false);
                  }}
                >
                  📄 Terms
                </button>
                
                <button 
                  className="mobile-menu-button"
                  onClick={() => {
                    setCurrentView('privacy');
                    setMobileMenuOpen(false);
                  }}
                >
                  🔒 Privacy
                </button>
              </div>
            </div>
          </>
        )}
          </div>
                {/* Sample Data Banner */}
                  {localStorage.getItem('taxme_using_sample') === 'true' && (
                    <div className="sample-data-banner">
                      <span className="sample-data-text">
                        🎯 <strong>Using Sample Data</strong>
                        <span className="sample-data-subtext"> - Explore all features with demo data</span>
                      </span>
                    </div>
                  )}
              {currentView === 'terms' ? (
                // Terms of Service View
                <div className="terms-page" style={{ 
                  maxWidth: '800px', 
                  margin: '40px auto', 
                  padding: '0 20px' 
                  }}>
                  <button 
                    onClick={() => setCurrentView('app')}
                    className="button button-secondary"
                    style={{ marginBottom: '20px' }}
                  >
                    ← Back to App
                  </button>
                  
                  <h1>Terms of Service</h1>
                    <p><strong>Last Updated: October 2025</strong></p>
                    
                    <h2>1. Acceptance of Terms</h2>
                    <p>By using the TaxMe - Belgian Capital Gains Tracker ("the Software"), you agree to these Terms of Service. If you do not agree, please do not use the Software.</p>
                    
                    <h2>2. Proprietary Rights</h2>
                    <p>This Software is proprietary and protected by copyright laws. All source code, algorithms, and intellectual property remain the exclusive property of the developer. You may not:</p>
                    <ul>
                      <li>Reverse engineer, decompile, or disassemble the Software</li>
                      <li>Copy, modify, or create derivative works</li>
                      <li>Distribute, sell, or sublicense the Software</li>
                      <li>Use the Software for commercial purposes without written permission</li>
                    </ul>
                    <p></p>
                    <h2>3. Disclaimer of Warranties</h2>
                    <p><strong>THE SOFTWARE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND.</strong> The developer makes no warranties about the accuracy, reliability, or completeness of tax calculations. Tax laws are complex and subject to interpretation.</p>
                    
                    <h2>4. Limitation of Liability</h2>
                    <p>You acknowledge that:</p>
                    <ul>
                      <li>Tax calculations are for informational purposes only</li>
                      <li>You should consult a qualified tax advisor for your specific situation</li>
                      <li>The developer is not liable for any tax penalties, interest, or losses</li>
                      <li>You use this Software at your own risk</li>
                    </ul>
                    <p></p>
                    <h2>5. User Responsibilities</h2>
                    <p>You are responsible for:</p>
                    <ul>
                      <li>The accuracy of data you import</li>
                      <li>Verifying all calculations before filing taxes</li>
                      <li>Compliance with Belgian tax laws</li>
                      <li>Maintaining backups of your data</li>
                    </ul>
                    <p></p>
                    <h2>6. Indemnification</h2>
                    <p>You agree to indemnify and hold harmless the developer from any claims, damages, or expenses arising from your use of the Software or reliance on its calculations.</p>
                    
                    <h2>7. Modifications</h2>
                    <p>These terms may be updated at any time. Continued use of the Software constitutes acceptance of updated terms.</p>
                    
                    <h2>8. Governing Law</h2>
                    <p>These terms are governed by Belgian law. Any disputes shall be resolved in the courts of Belgium.</p>
                    
                    <h2>9. Termination</h2>
                    <p>The developer reserves the right to terminate your access to the Software at any time for violation of these terms.</p>
                    
                    <h2>10. Contact</h2>
                    <p>For licensing inquiries or questions about these terms, please use the Share Feedback form on the main page.</p>
                  </div>
                ) : currentView === 'privacy' ? (
                // Privacy Policy View
                <div className="privacy-page" style={{ 
                  maxWidth: '800px', 
                  margin: '40px auto', 
                  padding: '0 20px' 
                  }}>
                  <button 
                    onClick={() => setCurrentView('app')}
                    className="button button-secondary"
                    style={{ marginBottom: '20px' }}
                  >
                    ← Back to App
                  </button>
                  
                  <h1>Privacy Policy</h1>
                      <p><strong>Last Updated: October 2025</strong></p>

                      <h2>Your Data Stays on Your Device</h2>
                      <ul>
                        <li>This app runs entirely in your browser. All financial data you import, positions, trades, and tax calculations, is stored locally on your device.</li>
                        <li>We do not collect, store, or transmit any of your personal or financial information to our servers.</li>
                        <li>Authentication (if used) is handled by Clerk, but your financial data never leaves your browser.</li>
                      </ul>

                      <h2>Data Persistence and Export</h2>
                      <ul>
                        <li>Your data persists only in your browser's local storage. If you clear your browser data or use a different device, your data will be lost.</li>
                        <li>You are responsible for exporting your data regularly using the Excel export feature (sign-in required).</li>
                        <li>Exported files contain sensitive financial information, store them securely and never share publicly.</li>
                      </ul>

                      <h2>Security and Your Responsibility</h2>
                      <ul>
                        <li>Data security depends on your device. Avoid using shared or public computers for this app.</li>
                        <li>We cannot recover lost data. If you clear your browser storage without exporting, your data is permanently gone.</li>
                        <li>Use strong device passwords and keep your browser updated for optimal security.</li>
                      </ul>

                      <h2>Authentication</h2>
                      <p>If you choose to sign in:</p>
                      <ul>
                        <li>Authentication is handled by Clerk, a secure third-party service.</li>
                        <li>We store only your email address for account identification.</li>
                        <li>Your financial data remains in your browser and is never sent to our servers.</li>
                      </ul>

                      <h2>Third-Party Scripts</h2>
                      <ul>
                        <li>We do not use analytics or advertising scripts that could access your financial data.</li>
                        <li>Authentication uses Clerk's secure infrastructure.</li>
                        <li>No third-party has access to your financial information.</li>
                      </ul>

                      <h2>Your Rights Under GDPR</h2>
                      <p>For EU users, you have the right to:</p>
                      <ul>
                        <li>Access your account information (email only, as financial data is local)</li>
                        <li>Delete your account at any time through the user menu</li>
                        <li>Data portability (export your financial data via Excel)</li>
                      </ul>

                      <h2>Cookies</h2>
                      <ul>
                        <li>Essential cookies only for authentication (if signed in)</li>
                        <li>No tracking or advertising cookies</li>
                        <li>Local storage for your financial data (not cookies)</li>
                      </ul>

                      <h2>Important Disclaimer</h2>
                      <ul>
                        <li>This app is for personal tax calculation and record-keeping only.</li>
                        <li>You are responsible for ensuring your tax reporting complies with Belgian law.</li>
                        <li>Always consult a qualified tax advisor for your specific situation.</li>
                      </ul>

                      <h2>Children's Privacy</h2>
                      <p>This service is not intended for users under 18 years of age.</p>

                      <h2>Changes to This Policy</h2>
                      <p>We may update this privacy policy. Significant changes will be communicated to registered members via email.</p>

                      <h2>Contact</h2>
                      <p>For privacy concerns or data requests, please use the Share Feedback form on the main page.</p>
                  </div>
                ) : currentView === 'help' ? (
                  // Help/Documentation page
                    <div className="help-page" style={{ 
                    maxWidth: '800px', 
                    margin: '40px auto', 
                    padding: '0 20px',
                    minHeight: 'calc(100vh - 200px)'
                    }}>
                    <button 
                      onClick={() => setCurrentView('app')}
                      className="button button-secondary"
                      style={{ marginBottom: '20px' }}
                    >
                      ← Back to App
                    </button>
                    
              <h1>Help & Documentation</h1>

              <h2>Import File Requirements</h2>

              <h3>Positions File (Year-End Holdings)</h3>
              <p>Your positions CSV file must contain the following columns (the tool recognizes various column name formats):</p>
              <ul>
                <li><strong>Symbol</strong> - Stock ticker (accepts: Symbol, Ticker, Security)</li>
                <li><strong>Quantity</strong> - Number of shares held (accepts: Quantity, Qty, Shares, Position)</li>
                <li><strong>Market Price</strong> - Current price per share (accepts: MarkPrice, Mark Price, Market Price, Current Price, Price)</li>
                <li><strong>Cost Basis Price</strong> - Original purchase price (accepts: CostBasisPrice, Cost Basis Price, Avg Cost, Average Cost)</li>
                <li><strong>Position Value</strong> - Total market value (accepts: PositionValue, Position Value, Market Value, Value)</li>
                <li><strong>Cost Basis Total</strong> - Total cost basis (accepts: CostBasisMoney, Cost Basis Money, Total Cost, Cost)</li>
                <li><strong>Currency</strong> - Currency of the position (accepts: CurrencyPrimary, Currency Primary, Currency, Curr)</li>
                <li><strong>FX Rate</strong> - Exchange rate to EUR on the Report Date (accepts: FXRateToBase, FX Rate To Base, FX Rate, Exchange Rate)</li>
                <li><strong>Asset Class</strong> - Type of asset (accepts: AssetClass, Asset Class, Type, Asset Type)</li>
                <li><strong>Report Date</strong> - Date of the snapshot (accepts: ReportDate, Report Date, Date, As Of Date)</li>
              </ul>
              <p><strong>Note:</strong></p>
              <ul>
                <li>The Report Date must be the last trading day of 2025 for accurate baseline calculations.</li>
                <li>If you started investing in 2026 or later, the baseline positions file is optional. You only need to upload your trade history.</li>
              </ul>

              <h4>Important: Single Account Limitation</h4>
              <p>This tool expects positions data from a <strong>single brokerage export</strong> where each symbol appears once:</p>
              <ul>
                <li>✅ <strong>Supported:</strong> Standard IB export (one row per symbol with averaged costs)</li>
                <li>❌ <strong>Not Supported:</strong> Multiple rows for the same symbol</li>
                <li>❌ <strong>Not Supported:</strong> Combining multiple account files</li>
              </ul>
              <p style={{ 
                padding: '10px', 
                backgroundColor: 'var(--color-warning-light)', 
                borderRadius: '4px' 
              }}>
                <strong>Multiple Accounts?</strong> Consolidate in Excel first: combine rows for 
                the same symbol, sum quantities, and calculate weighted average costs. 
              </p>

              <h3>Trades File (Buy/Sell Transactions)</h3>
              <p>Your trades CSV file must contain the following columns (the tool recognizes various column name formats):</p>
              <ul>
                <li><strong>Trade ID</strong> - Unique identifier (accepts: TradeID, Trade ID, ID, Order ID)</li>
                <li><strong>Symbol</strong> - Stock ticker (accepts: Symbol, Ticker, Security)</li>
                <li><strong>Trade Date</strong> - Date of transaction (accepts: TradeDate, Trade Date, Date, Transaction Date)</li>
                <li><strong>Buy/Sell</strong> - Transaction type (accepts: Buy/Sell, BuySell, Side, Action)</li>
                <li><strong>Quantity</strong> - Number of shares traded (accepts: Quantity, Qty, Shares, Amount)</li>
                <li><strong>Trade Price</strong> - Price per share at execution (accepts: TradePrice, Trade Price, Price, Execution Price)</li>
                <li><strong>Trade Money</strong> - Total transaction amount (accepts: TradeMoney, Trade Money, Total, Net Amount)</li>
                <li><strong>Currency</strong> - Transaction currency (accepts: CurrencyPrimary, Currency Primary, Currency)</li>
                <li><strong>FX Rate</strong> - Exchange rate to EUR on the Trade Date (accepts: FXRateToBase, FX Rate To Base, FX Rate, Exchange Rate)</li>
                <li><strong>Asset Class</strong> - Type of asset (accepts: AssetClass, Asset Class, Type)</li>
              </ul>

              <h2>Belgian CGT Tax Rules (2026-2030)</h2>
              <p>This tool implements the Belgian capital gains tax rules for the transition period 2026-2030:</p>
              <ul>
                <li><strong>Tax Rate:</strong> 10% on net realized gains</li>
                <li><strong>Annual Exemption:</strong> €10,000 per year (not cumulative)</li>
                <li><strong>Loss Treatment:</strong> Losses can only offset gains within the same calendar year - unused losses expire</li>
              </ul>
              <p style={{ fontSize: '0.9em', fontStyle: 'italic', color: 'var(--color-text-secondary)' }}>
                Note: The exemption amount is indexed annually to cost of living, and the tax rate may change with future legislation. The app uses year-specific configuration, so the correct values are automatically applied per tax year.
              </p>

              <h3>Special Rules for Pre-2026 Positions</h3>
              <p>Assets purchased before January 1, 2026 receive asymmetric treatment:</p>
              <ul>
                <li><strong>For Gains:</strong> Cost basis = MAX(Purchase Price, Dec 31 2025 Value). You benefit from the higher value to minimize tax.</li>
                <li><strong>For Losses:</strong> Cost basis = Dec 31 2025 Value (forced). Purchase price cannot be used, preventing pre-2026 loss claims.</li>
                <li><strong>Time Limit:</strong> The option to use purchase price for gains expires Dec 31, 2030.</li>
              </ul>

              <h3>Standard Rules for Post-2026 Purchases</h3>
              <ul>
                <li>Cost basis = Purchase Price (standard treatment)</li>
                <li>Full economic gain or loss is recognized for tax</li>
                <li>Losses can offset gains only within the same year (no carry-forward)</li>
                <li>No special baseline rules apply</li>
              </ul>

              <h2>Method & Calculation</h2>
              <ul>
                <li><strong>Scope:</strong> Stocks, bonds, ETFs, options, and crypto. Excludes physical commodities and insurance products.</li>
                <li><strong>FIFO Method:</strong> First In, First Out - oldest shares are sold first. Pre-2026 baseline positions are treated as the oldest lots, followed by 2026+ purchases in chronological order. Each lot retains its original purchase FX rate for accurate multi-currency gain calculations.</li>
                <li><strong>Currency Conversion:</strong> All amounts are converted to EUR using the exchange rate on each transaction date. Buy transactions use the FX rate at purchase; sell transactions use the FX rate at sale. These rates are imported from your broker's CSV export - ensure your export includes the FXRateToBase field for accurate multi-currency calculations.</li>
                <li><strong>Tax Formula:</strong> Tax = (Net Gains - Annual Exemption) × Tax Rate. For 2026: €10,000 exemption at 10% rate. Values may be adjusted in future years.</li>
                <li><strong>Date Formats:</strong> Flexible date parsing supports YYYY-MM-DD, YYYY/MM/DD, DD-MM-YYYY, DD/MM/YYYY, MM-DD-YYYY, MM/DD/YYYY, YYYYMMDD, and ISO datetime formats (YYYY-MM-DDTHH:mm:ss). When format is ambiguous (e.g., 01/02/2025), the tool assumes European format (DD/MM) given the Belgian context.</li>
              </ul>

              <h2>Known Limitations</h2>
              <p>This app covers standard buy/sell transactions for stocks, bonds, ETFs, options, and crypto. The following scenarios are not currently supported:</p>

              <h3>Out of Scope Assets</h3>
              <ul>
                <li><strong>Physical commodities</strong> - Silver, platinum, palladium are not subject to Belgian CGT. Physical gold IS taxable but this app only handles brokerage assets.</li>
                <li><strong>Insurance products</strong> - Tak 21/23/26 have specific valuation rules for premiums and reserves.</li>
              </ul>

              <h3>Not Implemented Features</h3>
              <ul>
                <li><strong>Exemption rollover</strong> - The €10,000 exemption can grow to €15,000 if unused for 5 consecutive years. This app applies only the standard annual exemption.</li>
                <li><strong>Written options</strong> - Premiums received from writing puts/calls fall under "divers inkomen" (miscellaneous income), not CGT. This app currently treats all option transactions as CGT.</li>
                <li><strong>Option exercise</strong> - When options are exercised (not traded), the strike price becomes the cost basis for acquired shares. This app only tracks option buy/sell transactions, not exercises.</li>
                <li><strong>Reynderstaks interaction</strong> - Bond and mixed funds (&gt;10% fixed income) are partially taxed under Reynderstaks at 30%. This app does not separate the two tax regimes.</li>
                <li><strong>Bonds held to maturity</strong> - Legal treatment is currently unclear whether maturity constitutes a taxable event.</li>
              </ul>

              <h3>Special Situations Not Handled</h3>
              <ul>
                <li><strong>Inheritance</strong> - Inherited assets retain the original cost basis from the deceased; heirs are taxed only on gains after Dec 31, 2025.</li>
                <li><strong>Investment clubs &amp; partnerships</strong> - Fiscally transparent structures where each member is taxed proportionally with their own €10,000 exemption.</li>
                <li><strong>DRIP shares</strong> - Dividend reinvestment plan shares use the actual market purchase price as cost basis.</li>
                <li><strong>Employee stock options</strong> - Cost basis equals market value at exercise, not the strike price (opposite of regular options).</li>
              </ul>

              <h3>Assumptions &amp; Technical Notes</h3>
              <ul>
                <li><strong>Gross gains</strong> - Belgian law taxes gross gains before broker fees and transaction taxes. This app assumes your broker's trade data reports gross values (Quantity × Price), which is standard for Interactive Brokers.</li>
                <li><strong>Crypto assets</strong> - This app treats crypto as any other asset subject to CGT. If your broker provides crypto transactions in the same CSV format, they will be processed with identical tax rules.</li>
                <li><strong>Baseline FX rates</strong> - For pre-2026 positions, only the December 31, 2025 exchange rate is available. Historical purchase FX rates are not captured in position snapshots. This may cause minor inaccuracies in EUR cost basis for foreign currency holdings acquired before 2026.</li>
                <li><strong>Same-day transactions</strong> - Multiple trades of the same asset on the same day are processed in the order they appear in your broker's export. This affects FIFO lot sequencing when same-day purchases have different prices.</li>
              </ul>

              <p style={{ marginTop: '20px', padding: '15px', backgroundColor: 'var(--color-warning-light)', borderRadius: '8px' }}>
                <strong>💡 Tip:</strong> If any of these scenarios apply to you, consult a tax advisor for those specific transactions.
              </p>

              <h2>How This App Works</h2>

              <h3>A Desktop Tool in Your Browser</h3>
              <p>
                While this is technically a "web application," it functions as a desktop tool that runs entirely in your browser. 
                Think of it like this: your browser is simply the engine that runs the software, similar to how Excel runs on Windows.
              </p>
              <ul>
                <li><strong>All calculations happen on your computer</strong> - No data is sent to servers for processing</li>
                <li><strong>Your financial data never leaves your device</strong> - Everything stays in your browser's storage</li>
                <li><strong>Works offline</strong> - Once loaded, no internet connection needed for calculations</li>
                <li><strong>No installation required</strong> - Access from any modern browser instantly</li>
                <li><strong>Automatic updates</strong> - Always get the latest tax rules when you visit</li>
              </ul>

              <h3>Best Practices</h3>
              <ul>
                <li><strong>Use on desktop/laptop</strong> - While accessible on mobile, this tool is designed for larger screens</li>
                <li><strong>Use Brave, Chrome, Firefox, or Edge</strong> - Modern browsers work best</li>
                <li><strong>Keep one browser</strong> - Your data is stored in the specific browser you use</li>
                <li><strong>Regular exports</strong> - Download your data periodically for backup</li>
              </ul>

              <h3>What This Means for Privacy</h3>
              <p>
                Unlike cloud-based tax software, your sensitive financial information stays completely under your control. 
                This is by design — Belgian tax data is personal, and we believe it should stay that way. The trade-off is 
                that you're responsible for backing up your data through regular exports.
              </p>

              <p style={{ marginTop: '20px', padding: '15px', backgroundColor: 'var(--color-warning-light)', borderRadius: '8px' }}>
                <strong>⚠️ Important:</strong> This tool is for informational purposes only and implements the rules as understood. Tax laws are complex and subject to interpretation. Always consult a qualified Belgian tax advisor for your specific situation.
              </p>
                  </div>
              ) : (
                <>              
                  {/* Notifications */}
               <div className="notifications-container">
                  {notifications.map((notification, index) => (
                    <div
                      key={notification.id}
                      className="notification-wrapper"
                      style={{
                        transform: `translateY(${index * -80}px)` // Negative for bottom positioning
                      }}
                    >
                  <Notification
                    notification={notification}
                    onClose={(id) => {
                      setNotifications(prev => prev.filter(n => n.id !== id));
                    }}
                  />
                </div>
              ))}
    </div>
      {/* Loading spinner */}       
      {isLoading && <LoadingSpinner message={loadingMessage} />}

      {/* Landing page or main page - only show when no data */}
      {positionsData.length === 0 && tradesData.length === 0 && (
            <div className="landing-page">
              <div className="tagline-section">
                <h1 className="tagline-main">The easiest way to understand Belgian capital gains tax</h1>
                <p className="tagline-sub">Import trades from your broker and explore your tax implications instantly</p>
              </div>
              
              {/* Your buttons will go here next */}
            </div>
      )}
      {/* Main app content when data is loaded */}

      {/* Control Center - Conditional Layout */}
{(() => {
  // Check if any data exists
  const hasAnyData = positionsData.length > 0 || tradesData.length > 0;
  
  if (!hasAnyData) {
    // EMPTY STATE: Only centered buttons
    return (
      <div className="control-center control-center-empty">
        <div className="control-center-empty-content">
          <label className="button button-primary upload-button" title="Upload your December 31, 2025 portfolio positions">
            📊 Upload Baseline Holdings
            <input
              type="file"
              accept=".csv"
              onChange={handlePositionsFileSelect}
              style={{display: 'none'}}
            />
          </label>
          
          <label className="button button-primary upload-button" title="Upload your trades from 2026 onwards">
            💰 Upload Your Trades
            <input
              type="file"
              accept=".csv"
              multiple
              onChange={handleTradesFileSelect}
              style={{display: 'none'}}
            />
          </label>
          
          <button 
            className="button button-secondary upload-button sample-data-button"
            onClick={loadSampleData}
            title="Load sample data to explore all features"
          >
            🎯 Try Sample Data
          </button>
        </div>
      </div>
    );
  }
  
// LOADED STATE: Clean control panel with info cards
return (    
  <>  
         {/* Triangle toggle - only show when both datasets are loaded */}
          {tradesData.length > 0 && (
            <button
              className="control-panel-toggle"
              onClick={() => setControlPanelExpanded(!controlPanelExpanded)}
              title={controlPanelExpanded ? "Hide data controls" : "Show data controls"}
              aria-label={controlPanelExpanded ? "Hide data controls" : "Show data controls"}
              aria-expanded={controlPanelExpanded}
              aria-controls="control-panel"
            >
              <div className={`triangle ${controlPanelExpanded ? 'triangle-up' : 'triangle-down'}`}></div>
              <div className={`triangle ${controlPanelExpanded ? 'triangle-up' : 'triangle-down'}`}></div>
            </button>
          )}   

        <div className={`control-panel${controlPanelExpanded ? '' : ' collapsed'}`}>
          <div className="control-panel-inner">
            
            {/* Row 1: Positions Info Card - ALWAYS SHOWS */}
            <div className="control-info-card">
              {positionsData.length > 0 ? (
                // Has positions: Show info + update + clear
                <>
                  <div className="control-info-text">
                    <span className="control-info-icon">📊</span>
                    <span className="control-info-details">
                      <span className="control-text-desktop">Positions • </span>
                      {positionsData.length} position{positionsData.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="control-info-actions">
                    <label 
                      className="button button-secondary control-update-button"
                      title="Update positions data"
                    >
                      ↻ Update
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handlePositionsFileSelect}
                        style={{display: 'none'}}
                      />
                    </label>
                    <button 
                      className="control-clear-icon"
                      onClick={clearPositionsFromStorage}
                      title="Clear positions data"
                      aria-label="Clear positions"
                    >
                      🗑️
                    </button>
                  </div>
                </>
              ) : (
                // No positions: Show upload + disabled clear
                <>
                  <div className="control-info-text">
                    <span className="control-info-icon">📊</span>
                    <span className="control-info-details">
                      No positions loaded
                    </span>
                  </div>
                  <div className="control-info-actions">
                    <label 
                      className="button button-primary control-update-button"
                      title="Upload baseline positions"
                    >
                      📤 Upload
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handlePositionsFileSelect}
                        style={{display: 'none'}}
                      />
                    </label>
                    <button 
                      className="control-clear-icon"
                      disabled
                      title="Upload positions first to enable clear"
                      aria-label="Clear disabled - no positions loaded"
                    >
                      🗑️
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Row 2: Trades Info Card - ALWAYS SHOWS */}
            <div className="control-info-card">
              {tradesData.length > 0 ? (
                // Has trades: Show info + update + clear
                <>
                  <div className="control-info-text">
                    <span className="control-info-icon">📈</span>
                    <span className="control-info-details">
                      <span className="control-text-desktop">Trades • </span>
                      {(() => {
                        // Calculate counts excluding CASH
                        const nonCashTrades = tradesData.filter(t => t.AssetClass !== 'CASH');
                        return `${nonCashTrades.length} trade${nonCashTrades.length !== 1 ? 's' : ''}`;
                      })()}
                    </span>
                  </div>
                  <div className="control-info-actions">
                    <label 
                      className="button button-secondary control-update-button"
                      title="Update trades data"
                    >
                      ↻ Update
                      <input
                        type="file"
                        accept=".csv"
                        multiple
                        onChange={handleTradesFileSelect}
                        style={{display: 'none'}}
                      />
                    </label>
                    <button 
                      className="control-clear-icon"
                      onClick={clearTradesFromStorage}
                      title="Clear trades data"
                      aria-label="Clear trades"
                    >
                      🗑️
                    </button>
                  </div>
                </>
              ) : (
                // No trades: Show upload + disabled clear
                <>
                  <div className="control-info-text">
                    <span className="control-info-icon">📈</span>
                    <span className="control-info-details">
                      No trades loaded
                    </span>
                  </div>
                  <div className="control-info-actions">
                    <label 
                      className="button button-primary control-update-button"
                      title="Upload trades"
                    >
                      📤 Upload
                      <input
                        type="file"
                        accept=".csv"
                        multiple
                        onChange={handleTradesFileSelect}
                        style={{display: 'none'}}
                      />
                    </label>
                    <button 
                      className="control-clear-icon"
                      disabled
                      title="Upload trades first to enable clear"
                      aria-label="Clear disabled - no trades loaded"
                    >
                      🗑️
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Row 3: Clear All Button - Only show if ANY data exists */}
            {(positionsData.length > 0 || tradesData.length > 0 || dataLoadStatus) && (
              <button 
                className="control-clear-button"
                onClick={clearAllData}
              >
                <span>🗑️</span>
                <span>Clear All Data</span>
              </button>
            )}
            
          </div>
        </div>
        </>
      );
      })()}
               <main className="main-content">
                {/* TAB NAVIGATION - EMPTY STATE (LANDING PAGE) */}
                {positionsData.length === 0 && tradesData.length === 0 && (
                <>
                  {/* Three Simple Steps */}
                  <div className="guidelines-section" ref={guidelinesRef}>
                    <h2 className="guidelines-title">Three Simple Steps</h2>
                    <div className="guidelines-columns">
                          {[
                          {
                            number: 1,
                            title: "Export Your Data",
                            description: "Download your December 31, 2025 holdings and 2026+ trades from your broker as CSV files"
                          },
                          {
                            number: 2,
                            title: "Upload Your Files",
                            description: "First upload your baseline holdings, then your trade history. Files are validated automatically"
                          },
                          {
                            number: 3,
                            title: "Get Your Tax Insights",
                            description: "See your exact Belgian capital gains tax insights with transaction-level detail and €10,000 exemption applied"
                          }
                          ].map((step) => (
                            <div key={step.number} className="guideline-item">
                              <div className={`guideline-number-outline ${hasAnimated ? `auto-animate-${step.number}` : ''}`}
                                data-step={step.number}
                                onAnimationEnd={(e) => {
                                  if (e.animationName === 'fadeToBlue') {
                                    e.currentTarget.classList.remove(`auto-animate-${step.number}`);
                                  }
                                }}
                                >
                                {step.number}
                              </div>
                              <h3 className="guideline-heading">{step.title}</h3>
                              <p className="guideline-text">{step.description}</p>
                            </div>
                          ))}
                    </div>
                  </div>
                  {/* USPs Section */}
                  <div className="usps-section" ref={uspSectionRef}>
                    <div className="usps-container">
                      <div className="usp-item">
                        <div 
                            className={`usp-icon ${uspAnimated ? 'animate' : ''}`}
                            data-icon="1"
                            style={{ position: 'relative' }}
                            >
                            <svg className="usp-icon" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                              <path className="icon-stroke" d="M37.2133,17.7292A13.2133,13.2133,0,1,0,16.5058,28.5958a4.0061,4.0061,0,0,1,1.6648,3.315v4.7315H29.8294V31.907A4.0511,4.0511,0,0,1,31.532,28.57,13.1741,13.1741,0,0,0,37.2133,17.7292Z"/>
                              <line className="icon-stroke" x1="19.4437" y1="38.9282" x2="28.5563" y2="38.9282"/>
                              <line className="icon-stroke" x1="19.3782" y1="41.2141" x2="28.4908" y2="41.2141"/>
                              <line className="icon-stroke" x1="21.5213" y1="43.5" x2="26.4787" y2="43.5"/>
                              <polyline className="icon-stroke" points="26.011 36.642 26.011 29.583 27.66 26.085"/>
                              <polyline className="icon-stroke" points="21.989 36.642 21.989 29.583 20.34 26.085"/>
                            </svg>
                        </div>
                        <h3 className="usp-title">Tax Insights</h3>
                        <p className="usp-text">See how Belgian capital gains legislation might impact your investment proceeds. Detailed transaction-level analysis for every trade.</p>
                      </div>
        
                      <div className="usp-item">
                        <div className={`usp-icon ${uspAnimated ? 'animate' : ''}`}
                            data-icon="2"
                            style={{ position: 'relative' }}
                            >
                            <svg className="usp-icon" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                              <path className="icon-stroke" d="m24,43.5c9.0432-3.1174,15.4885-10.3631,16.5-19.5889.2804-4.0049.2566-8.0254-.0714-12.0267-.0898-1.3069-1.1582-2.3314-2.4677-2.3663-4.0911-.126-8.8455-.8077-12.52-4.4273-.7988-.7877-2.0822-.7877-2.881,0-3.6749,3.6192-8.4293,4.3013-12.5199,4.4273-1.3095.0349-2.3779,1.0594-2.4677,2.3663-.3283,4.0013-.3524,8.0217-.0723,12.0267,1.0115,9.2258,7.4568,16.4715,16.5,19.5889Z"/>
                              <path className="icon-stroke" d="m18.322,21.0476h11.3561c.4307,0,.7798.3491.7798.7798v8.3927c0,.4307-.3491.7798-.7798.7798h-11.3561c-.4307,0-.7799-.3492-.7799-.7799v-8.3927c0-.4307.3491-.7798.7798-.7798h0Z"/>
                              <path className="icon-stroke" d="m19.7258,21.0476v-1.7902c.0073-2.3586,1.9252-4.2647,4.2838-4.2574,2.3483.0072,4.2502,1.9091,4.2574,4.2574v1.7902"/>
                              <circle className="icon-stroke" cx="23.9963" cy="26.0238" r="1.6563"/>
                            </svg>
                        </div>
                        <h3 className="usp-title">Private & Secure</h3>
                        <p className="usp-text">Your financial data stays on your device. All calculations run locally in your browser - nothing is sent to our servers or stored online.</p>
                      </div>
        
                      <div className="usp-item">
                        <div className={`usp-icon ${uspAnimated ? 'animate' : ''}`}
                            data-icon="3"
                            style={{ position: 'relative' }}
                            >
                            <svg className="usp-icon" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                              <path className="icon-stroke" d="M5.15,5.8009c2.8164-1.4129,9.8183-1.4443,12.628,0-.0021,4.4444-.0047,10.363-6.314,13.2594C5.1631,16.11,5.15,10.2416,5.15,5.8009Z"/>
                              <path className="icon-stroke" d="M29.3957,40.5279H13.99a2.8413,2.8413,0,0,1-2.5256-2.5256V19.06M17.778,7.6951H34.1944A2.8414,2.8414,0,0,1,36.72,10.2207V27.2685"/>
                              <circle className="icon-stroke" cx="34.5732" cy="34.8453" r="7.5768"/>
                              <path className="icon-stroke" d="M30.1989,35.23l2.7494,2.7548L38.9134,31.8"/>
                              <path className="icon-stroke" d="M30.406,25.3743H25.3548V20.3231H30.406Z"/>
                              <path className="icon-stroke" d="M17.778,30.2992H28.5118m-8.84,4.1672H26.807"/>
                              <path className="icon-stroke" d="M22.8292,12.7463v5.0512H17.778"/>
                              <path className="icon-stroke" d="M25.3548,12.7463H30.406v5.0512H25.3548Z"/>
                              <path className="icon-stroke" d="M17.778,20.3231h5.0512v5.0512H17.778Z"/>
                              <path className="icon-stroke" d="M11.464,7.4425v7.5768M7.6756,11.1047h7.5768"/>
                            </svg>
                        </div>
                          <h3 className="usp-title">Full Transparency</h3>
                          <p className="usp-text">View individual trade impacts, not just yearly totals. Track exactly how each buy and sell contributes to your €10,000 annual exemption usage throughout the year.</p>
                      </div>
                    </div>
                  </div>
                  {/* Help Card */}
                    <div className="help-card">
                      <div className="help-card-content">
                        <div className="help-card-text-section">
                          <h3 className="help-card-title">Help us help you better</h3>
                          <p className="help-card-text">
                            Built by a Belgian investor facing the same tax challenges. 
                            Tell us your broker, your tax questions, or features that would help. Your input shapes what we build next.
                          </p>
                        </div>
                        <button 
                          onClick={() => setShowContactForm(true)}
                          className="help-card-button"
                        >
                          Share Feedback
                        </button>
                      </div>
                    </div>
                </>
              )}          
                {/* TAB NAVIGATION - LOADED STATE */}
                {(positionsData.length > 0 || tradesData.length > 0) && ( 
                  <>
                  <div className="tab-navigation" style={{
                    marginTop: 'var(--space-4)',
                    marginBottom: 'var(--space-4)',
                    borderBottom: '2px solid var(--color-border)',
                    position: 'relative'
                  }}>
                    <div style={{
                      display: 'flex',
                      gap: 'var(--space-2)',
                      overflowX: 'auto',  // Allows horizontal scroll on mobile
                      WebkitOverflowScrolling: 'touch',  // Smooth scroll on iOS
                      scrollbarWidth: 'none',  // Hide scrollbar Firefox
                      msOverflowStyle: 'none',  // Hide scrollbar IE
                    }}>
                      {tabs.map(tab => (
                   <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      className={`tab-button ${activeTab === tab.id ? 'tab-button-active' : ''}`}
                    >
                          <span style={{ marginRight: 'var(--space-1)' }}>{tab.icon}</span>
                          {tab.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Tab Content Area */}
                  <div className="tab-content">
                     {/* Tax Tab */}
                          {activeTab === 0 && (
                          <div className="tab-content">
                            {/* Check for required data */}
                              {(!tradesData.length) ? (
                                <EmptyState icon="🧾" />
                              ) : (
                                <>
                            
                             <div style={{
                              padding: '10px',
                              backgroundColor: 'var(--color-warning-light)',
                              borderRadius: 'var(--radius-md)',
                              marginBottom: 'var(--space-3)',
                              fontSize: '0.9em',
                              color: 'var(--color-text-secondary)'
                            }}>
                              ⚠️ These calculations are for informational purposes only. Consult a tax professional before filing. 
                              <a href="#terms" onClick={(e) => { e.preventDefault(); setCurrentView('terms'); }}> See full disclaimer</a>
                            </div>
                              {/* Summary Card */}
                              <div style={{
                                backgroundColor: 'var(--color-background-secondary)',
                                borderRadius: 'var(--radius-lg)',
                                padding: 'var(--space-4)',
                                marginBottom: 'var(--space-4)',
                                position: 'relative'
                                }}>
                                <h3>
                                  <YearSelector
                                    selectedYear={selectedYears[0]}
                                    onYearChange={(year) => setSelectedYears([year])}
                                    availableYears={Object.keys(taxData.summaries || {}).sort()}
                                  />
                                  Tax Summary
                                </h3>  
                                {/* Export button - for ANY signed-in user */}
                                {isSignedIn && selectedYears?.[0] && taxData.summaries?.[selectedYears[0]] && (
                                  <button
                                    onClick={exportToExcel}
                                    className="button button-small"
                                    style={{ 
                                      position: 'absolute',
                                      top: 'var(--space-4)',
                                      right: 'var(--space-4)',
                                      padding: '4px 10px',
                                      fontSize: '13px'
                                    }}
                                  >
                                    📊 Export
                                  </button>
                                )}
                                {selectedYears && selectedYears[0] && taxData.summaries?.[selectedYears[0]] && (
                                  <div className="tax-summary-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
                                    {/* Total Realized Gains */}
                                    <div className="tax-summary-metric">
                                      <div className="tax-summary-label">Total Realized Gains</div>
                                      <div className="tax-summary-value">
                                        {formatCurrency(taxData.summaries[selectedYears[0]].totalRealizedGains || 0)}
                                      </div>
                                    </div>

                                   {/* Taxable Portion */}
                                      <div className="tax-summary-metric">
                                        <div className="tax-summary-label">Taxable Portion</div>
                                        <div className={`tax-summary-value ${
                                          taxData.summaries[selectedYears[0]].net < 0 
                                            ? 'tax-loss-warning' 
                                            : 'tax-positive'
                                        }`}>
                                          {formatCurrency(taxData.summaries[selectedYears[0]].net)}
                                          {taxData.summaries[selectedYears[0]].net < 0 && (
                                            <span 
                                              className="tax-loss-icon"
                                              data-tooltip={`€${Math.abs(taxData.summaries[selectedYears[0]].net).toFixed(2)} in losses will expire Dec 31. Belgian law does not allow loss carry-forward.`}
                                            >
                                              ⚠️
                                            </span>
                                          )}
                                        </div>
                                      </div>

                                    {/* Annual Exemption */}
                                    <div className="tax-summary-metric">
                                      <div className="tax-summary-label">Annual Exemption ({formatCurrency(taxData.summaries[selectedYears[0]].exemptionLimit)})</div>
                                      <div className="tax-summary-value">
                                        {formatCurrency(taxData.summaries[selectedYears[0]].exemptionUsed).split('.')[0]}
                                        <span style={{ fontSize: '0.6em', opacity: 0.6, marginLeft: '4px' }}>
                                           {taxData.summaries[selectedYears[0]].exemptionPercentage}%
                                        </span>
                                      </div>
                                    </div>

                                    {/* Subject to Tax */}
                                    <div className="tax-summary-metric">
                                      <div className="tax-summary-label">Subject to Tax</div>
                                      <div className="tax-summary-value" style={{
                                        color: taxData.summaries[selectedYears[0]].taxable === 0 
                                          ? 'var(--color-success)' 
                                          : 'var(--color-text-primary)'
                                      }}>
                                        {formatCurrency(taxData.summaries[selectedYears[0]].taxable)}
                                      </div>
                                    </div>

                                    {/* Tax Due */}
                                    <div className="tax-summary-metric">
                                      <div className="tax-summary-label">Tax Due ({(taxData.summaries[selectedYears[0]].taxRate * 100).toFixed(0)}%)</div>
                                      <div className="tax-summary-value" style={{
                                        color: taxData.summaries[selectedYears[0]].taxDue === 0 
                                          ? 'var(--color-success)' 
                                          : 'var(--color-warning)'
                                      }}>
                                        {formatCurrency(taxData.summaries[selectedYears[0]].taxDue)}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            {/* Transaction Table */}
                              {!taxData.transactionsByYear?.[selectedYears[0]] || taxData.transactionsByYear[selectedYears[0]].length === 0 ? (
                                <div style={{
                                  textAlign: 'center',
                                  padding: 'var(--space-6)',
                                  backgroundColor: 'var(--color-background-secondary)',
                                  borderRadius: 'var(--radius-lg)',
                                  margin: 'var(--space-4) 0'
                                }}>
                                  <div style={{ fontSize: '2.5em', marginBottom: 'var(--space-3)', opacity: 0.5 }}>📅</div>
                                  <h4 style={{ marginBottom: 'var(--space-2)' }}>No Taxable Trades Found</h4>
                                  <p style={{
                                    color: 'var(--color-text-secondary)',
                                    marginBottom: 0
                                  }}>
                                    To explore your tax implications, ensure both your baseline positions and trade history are uploaded.
                                  </p>
                                </div>
                              ) : (
                                <div className="table-container">
                                  <div className="table-scroll">
                                    <table className="data-table">
                                <thead>
                                    <tr>
                                      <th>Instrument</th>
                                      <th className="text-right">Quantity</th>
                                      <th className="text-right" title="Your actual profit or loss on this sale">
                                        Actual P&L
                                      </th>
                                      <th className="text-right" title="The portion subject to capital gains tax after applying Belgian tax rules">
                                        Amount Subject to Tax
                                      </th>
                                      <th className="text-right" title="Running total of all taxable amounts for the year">
                                        YTD Cumulative Taxable
                                      </th>
                                      <th className="text-center" title="Belgian tax rule applied to this transaction:
                                        - PROTECTED - Pre-2026 gains up to baseline are tax-free
                                        - FORCED - Pre-2026 losses must use baseline price
                                        - MAX - Purchase price used when higher than baseline
                                        - POST-26 - Standard rules for positions acquired after 2026
                                        - EXPIRED - Sold after 2030, baseline required for all pre-2026 positions">
                                          Tax Rule
                                        </th>
                                      <th className="text-center">Sale Date</th>
                                    </tr>
                                  </thead>
                                      <tbody>
                                        {taxData.transactionsByYear[selectedYears[0]].map((transaction) => (
                                          <tr key={`${transaction.symbol}-${transaction.tradeDate}-${transaction.quantity}`}>
                                            <td>{transaction.symbol}</td>
                                            <td className="text-right">{getDisplayQuantity(transaction)}</td>
                                            <td className="text-right">
                                              <span style={{ color: transaction.realGainLoss >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
                                                {formatCurrency(transaction.realGainLoss)}
                                              </span>
                                            </td>
                                            <td className="text-right">
                                              <span style={{ color: transaction.taxableGainLoss >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
                                                {formatCurrency(transaction.taxableGainLoss)}
                                              </span>
                                            </td>
                                            <td className="text-right">
                                              <span style={{ color: transaction.runningNet >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
                                                {formatCurrency(transaction.runningNet)}
                                              </span>
                                            </td>
                                          <td className="text-center">
                                            <span 
                                                className={`tax-status ${transaction.taxRuleStatus.toLowerCase()}`}
                                                title={transaction.taxRuleTooltip} 
                                              >
                                                {transaction.taxRuleStatus}
                                              </span>
                                            </td>
                                            <td className="text-center">{formatDateForDisplay(transaction.tradeDate)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                </div>
                              </div>
                            )}
                            </>
                            )}
                          </div>
                        )}
                        {/* Holdings Tab */}             
                        {activeTab === 1 && (
                          <div>
                            {/* Check for required data */}
                            {(!tradesData.length) ? (
                              <EmptyState icon="📈" />
                            ) : (
                              <>
                          {/* Check if no active holdings */}
                          {holdingsData.holdings.filter(h => h.status === 'ACTIVE').length === 0 ? (
                            <div style={{
                              textAlign: 'center',
                              padding: 'var(--space-6)',
                              backgroundColor: 'var(--color-background-secondary)',
                              borderRadius: 'var(--radius-lg)',
                              margin: 'var(--space-4) 0'
                            }}>
                              <div style={{ fontSize: '2.5em', marginBottom: 'var(--space-3)', opacity: 0.5 }}>💼</div>
                              <h4 style={{ marginBottom: 'var(--space-2)' }}>No Active Holdings Found</h4>
                              <p style={{
                                color: 'var(--color-text-secondary)',
                                marginBottom: 0
                              }}>
                                To explore your tax implications, ensure both your baseline positions and trade history are uploaded.
                              </p>
                            </div>
                          ) : (
                            <div>
                              {/* Current Holdings Summary Card */}
                                {(() => {
                                  const holdingsMetrics = calculateHoldingsMetrics(
                                    holdingsData.holdings,
                                    positionsData,
                                    userCurrency
                                  );
                                  
                                  return (
                                    <div className="overview-metrics-grid" style={{
                                      gridTemplateColumns: 'repeat(4, 1fr)',
                                      marginBottom: 'var(--space-4)',
                                      padding: 'var(--space-3)',
                                      backgroundColor: 'var(--color-background-secondary)',
                                      borderRadius: 'var(--radius-lg)',
                                    }}>
                                      <div>
                                        <div 
                                          style={{fontSize: '0.9em', color: 'var(--color-text-secondary)', marginBottom: '4px'}}
                                          title="Total cost basis - the sum of all amounts paid to purchase your current holdings"
                                        >
                                          Total Cost Basis
                                        </div>
                                        <div style={{fontSize: '1.4em', fontWeight: 'bold'}}>
                                          {formatCurrency(holdingsMetrics.totalCost, userCurrency)}
                                        </div>
                                      </div>

                                      <div>
                                        <div 
                                          style={{fontSize: '0.9em', color: 'var(--color-text-secondary)', marginBottom: '4px'}}
                                          title="Number of holdings with shares currently owned"
                                        >
                                          Active Positions
                                        </div>
                                        <div style={{fontSize: '1.4em', fontWeight: 'bold'}}>
                                          {holdingsMetrics.activeCount}
                                        </div>
                                      </div>

                                      <div>
                                        <div 
                                          style={{fontSize: '0.9em', color: 'var(--color-text-secondary)', marginBottom: '4px'}}
                                          title="Percentage of your total cost basis invested in your base currency. Based on purchase prices, not current values. Lower % means higher foreign exchange exposure"
                                        >
                                          Base Currency
                                        </div>
                                        <div style={{fontSize: '1.4em', fontWeight: 'bold'}}>
                                          {holdingsMetrics.baseCurrencyPercentage}% {userCurrency}
                                        </div>
                                      </div>

                                      <div>
                                        <div 
                                          style={{fontSize: '0.9em', color: 'var(--color-text-secondary)', marginBottom: '4px'}}
                                          title="Number of different asset types in portfolio (Stocks, Options, Crypto, etc.)"
                                        >
                                          Asset Diversity
                                        </div>
                                        <div style={{fontSize: '1.4em', fontWeight: 'bold'}}>
                                          {holdingsMetrics.assetClassCount} {holdingsMetrics.assetClassCount === 1 ? 'Class' : 'Classes'}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}
                        {/* Holdings Table */}
                            <div className="table-container">
                              <div className="table-scroll">
                                <table className="data-table">
                                  <thead>
                                      <tr>
                                        <th onClick={() => handleSort('symbol')} style={{cursor: 'pointer', fontWeight: sortColumn === 'symbol' ? 'bold' : 'normal', userSelect: 'none'}}>
                                          Instrument{getSortArrow('symbol')}
                                        </th>
                                        <th className="text-right" onClick={() => handleSort('currentQuantity')} style={{cursor: 'pointer', fontWeight: sortColumn === 'currentQuantity' ? 'bold' : 'normal', userSelect: 'none'}}>
                                          Quantity{getSortArrow('currentQuantity')}
                                        </th>
                                        <th className="text-right" onClick={() => handleSort('avgCost')} style={{cursor: 'pointer', fontWeight: sortColumn === 'avgCost' ? 'bold' : 'normal', userSelect: 'none'}}>
                                          Avg Cost{getSortArrow('avgCost')}
                                        </th>
                                        <th className="text-right" 
                                            onClick={() => handleSort('lastKnownPrice')} 
                                            style={{cursor: 'pointer', fontWeight: sortColumn === 'lastKnownPrice' ? 'bold' : 'normal', userSelect: 'none'}}
                                            title="Price from the most recent trade or Dec 31, 2025 baseline if no trades">
                                          Last Known Price{getSortArrow('lastKnownPrice')}
                                        </th>
                                        <th className="text-right" 
                                            title="Profit/Loss per share based on last known price vs average cost">
                                          P&L/Share
                                        </th>
                                        <th className="text-right" onClick={() => handleSort('totalCost')} style={{cursor: 'pointer', fontWeight: sortColumn === 'totalCost' ? 'bold' : 'normal', userSelect: 'none'}}>
                                          Total Cost (Trade){getSortArrow('totalCost')}
                                        </th>
                                        <th className="text-right" onClick={() => handleSort('totalCostBase')} style={{cursor: 'pointer', fontWeight: sortColumn === 'totalCostBase' ? 'bold' : 'normal', userSelect: 'none'}}>
                                          Total Cost (Local){getSortArrow('totalCostBase')}
                                        </th>
                                        <th className="text-right" 
                                            onClick={() => handleSort('lastKnownValue')} 
                                            style={{cursor: 'pointer', fontWeight: sortColumn === 'lastKnownValue' ? 'bold' : 'normal', userSelect: 'none'}}
                                            title="Current quantity × last known price in original currency">
                                          Last Known Value (Trade){getSortArrow('lastKnownValue')}
                                        </th>
                                        <th className="text-right" 
                                            onClick={() => handleSort('lastKnownValueBase')} 
                                            style={{cursor: 'pointer', fontWeight: sortColumn === 'lastKnownValueBase' ? 'bold' : 'normal', userSelect: 'none'}}
                                            title="Current quantity × last known price converted to your base currency">
                                          Last Known Value (Local){getSortArrow('lastKnownValueBase')}
                                        </th>
                                        <th className="text-right" 
                                            title="Potential profit/loss if sold at last known price (in original currency)">
                                          Unrealized P&L (Trade)
                                        </th>
                                        <th className="text-right" 
                                            onClick={() => handleSort('unrealizedPLBase')} 
                                            style={{cursor: 'pointer', fontWeight: sortColumn === 'unrealizedPLBase' ? 'bold' : 'normal', userSelect: 'none'}}
                                            title="Potential profit/loss if sold at last known price (in base currency)">
                                          Unrealized P&L (Local){getSortArrow('unrealizedPLBase')}
                                        </th>
                                        <th className="text-right" 
                                            title="Percentage gain/loss based on last known price vs cost basis (in original currency)">
                                          Unrealized Return % (Trade)
                                        </th>
                                        <th className="text-right" 
                                            onClick={() => handleSort('unrealizedReturnBase')} 
                                            style={{cursor: 'pointer', fontWeight: sortColumn === 'unrealizedReturnBase' ? 'bold' : 'normal', userSelect: 'none'}}
                                            title="Percentage gain/loss based on last known price vs cost basis (in base currency)">
                                          Unrealized Return % (Local){getSortArrow('unrealizedReturnBase')}
                                        </th>
                                        <th className="text-center">Currency</th>
                                        <th className="text-center">FX Rate</th>
                                        <th className="text-center">Asset Class</th>
                                        <th className="text-center">Sub Category</th>
                                        <th className="text-center">Country</th>
                                        <th className="text-center" 
                                            onClick={() => handleSort('lastActivityDate')} 
                                            style={{cursor: 'pointer', fontWeight: sortColumn === 'lastActivityDate' ? 'bold' : 'normal', userSelect: 'none'}}
                                            title="Date of most recent trade, or Dec 31, 2025 if no trades since baseline">
                                          Last Activity{getSortArrow('lastActivityDate')}
                                        </th>
                                      </tr>
                                    </thead>
                                  <tbody>
                                      {sortHoldings(holdingsData.holdings.filter(h => h.status === 'ACTIVE')).map((holding, index) => {
                                                                              
                                        
                                        return (
                                          <tr key={holding.symbol}>
                                            <td>{holding.symbol}</td>
                                            <td className="text-right">{getDisplayQuantity(holding)}</td>
                                            <td className="text-right">
                                              {formatCurrency(holding.avgCost, holding.currency)}
                                            </td>
                                            <td className="text-right">
                                              {formatCurrency(holding.lastKnownPrice, holding.currency)}
                                            </td>
                                            <td className="text-right">
                                              <span style={{
                                                color: (holding.lastKnownPrice - holding.avgCost) >= 0 ? 'var(--color-success)' : 'var(--color-danger)'
                                              }}>
                                                {formatCurrency(holding.lastKnownPrice - holding.avgCost, holding.currency)}
                                              </span>
                                            </td>
                                            <td className="text-right">
                                              {formatCurrency(holding.currentQuantity * holding.avgCost, holding.currency)}
                                            </td>
                                            <td className="text-right">
                                              {formatCurrency(holding.totalCostBase, userCurrency)}
                                            </td>
                                            <td className="text-right">
                                              {formatCurrency(holding.lastKnownValue, holding.currency)}
                                            </td>
                                            <td className="text-right">
                                              {formatCurrency(holding.lastKnownValueBase, userCurrency)}
                                            </td>
                                            <td className="text-right">
                                              <span style={{
                                                color: holding.unrealizedPL >= 0 ? 'var(--color-success)' : 'var(--color-danger)',
                                                fontWeight: 'var(--font-weight-medium)'
                                              }}>
                                                {formatCurrency(holding.unrealizedPL, holding.currency)}
                                              </span>
                                            </td>
                                            <td className="text-right">
                                              <span style={{
                                                color: holding.unrealizedPLBase >= 0 ? 'var(--color-success)' : 'var(--color-danger)',
                                                fontWeight: 'var(--font-weight-medium)'
                                              }}>
                                                {formatCurrency(holding.unrealizedPLBase, userCurrency)}
                                              </span>
                                            </td>
                                            <td className="text-right">
                                              <span style={{
                                                color: holding.unrealizedReturn >= 0 ? 'var(--color-success)' : 'var(--color-danger)'
                                              }}>
                                                {holding.unrealizedReturn.toFixed(1)}%
                                              </span>
                                            </td>
                                            <td className="text-right">
                                              <span style={{
                                                color: holding.unrealizedReturnBase >= 0 ? 'var(--color-success)' : 'var(--color-danger)'
                                              }}>
                                                {holding.unrealizedReturnBase.toFixed(1)}%
                                              </span>
                                            </td>
                                            <td className="text-center">{holding.currency}</td>
                                            <td className="text-center">{parseFloat(holding.fxRate).toFixed(5)}</td>
                                            <td className="text-center">{holding.assetClass || '-'}</td>
                                            <td className="text-center">{holding.subCategory || '-'}</td>
                                            <td className="text-center">{holding.country || '-'}</td>
                                            <td>{formatDateForDisplay(holding.lastActivityDate)}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                </table>
                              </div>
                            </div>
                  </div>
                   )}
                        </>
                        )}
                      </div>
                    )}
                    {/* Trades Tab */}
                            {activeTab === 2 && (
                              <div className="tab-content">
                                {/* Check for required data */}
                                {(!tradesData.length) ? (
                                  <EmptyState icon="💰" />
                                ) : (
                                  <>
                            {/* Trades Summary Card */}
                            <div style={{
                              backgroundColor: 'var(--color-background-secondary)',
                              borderRadius: 'var(--radius-lg)',
                              padding: 'var(--space-4)',
                              marginBottom: 'var(--space-4)',
                            }}>
                              <h3>
                                <YearSelector
                                  selectedYear={selectedYears[0]}
                                  onYearChange={(year) => setSelectedYears([year])}
                                  availableYears={availableYears}
                                />
                                Trades Summary
                              </h3>
                              
                              <div className="overview-metrics-grid">
                                <div style={{textAlign: 'center'}}>
                                  <div style={{fontSize: '0.9em', color: 'var(--color-text-secondary)'}}>Total Trades</div>
                                  <div style={{fontSize: '1.5em', fontWeight: 'bold', marginTop: '4px'}}>
                                    {getFilteredTrades().length}
                                  </div>
                                </div>
                                <div style={{textAlign: 'center'}}>
                                  <div style={{fontSize: '0.9em', color: 'var(--color-text-secondary)'}}>Symbols</div>
                                  <div style={{fontSize: '1.5em', fontWeight: 'bold', marginTop: '4px'}}>
                                    {new Set(getFilteredTrades().map(t => t.Symbol)).size}
                                  </div>
                                </div>
                                <div style={{textAlign: 'center'}}>
                                  <div style={{fontSize: '0.9em', color: 'var(--color-text-secondary)'}}>Buy Orders</div>
                                  <div style={{fontSize: '1.5em', fontWeight: 'bold', marginTop: '4px', color: 'var(--color-success)'}}>
                                    {getFilteredTrades().filter(t => t['Buy/Sell'] === 'BUY').length}
                                  </div>
                                </div>
                                <div style={{textAlign: 'center'}}>
                                  <div style={{fontSize: '0.9em', color: 'var(--color-text-secondary)'}}>Sell Orders</div>
                                  <div style={{fontSize: '1.5em', fontWeight: 'bold', marginTop: '4px', color: 'var(--color-danger)'}}>
                                    {getFilteredTrades().filter(t => t['Buy/Sell'] === 'SELL').length}
                                  </div>
                                </div>
                              </div>
                            </div>

                          {/* Trades Table */}
                          <div className="table-container">
                            <div className="table-scroll">
                              <table className="data-table">
                             <thead>
                              <tr>
                                <th onClick={() => handleSort('Symbol')} style={{cursor: 'pointer', fontWeight: sortColumn === 'Symbol' ? 'bold' : 'normal', userSelect: 'none'}}>
                                  Instrument{getSortArrow('Symbol')}
                                </th>
                                <th onClick={() => handleSort('Buy/Sell')} style={{cursor: 'pointer', fontWeight: sortColumn === 'Buy/Sell' ? 'bold' : 'normal', userSelect: 'none'}}>
                                  Buy/Sell{getSortArrow('Buy/Sell')}
                                </th>
                                <th className="text-right" onClick={() => handleSort('Quantity')} style={{cursor: 'pointer', fontWeight: sortColumn === 'Quantity' ? 'bold' : 'normal', userSelect: 'none'}}>
                                  Quantity{getSortArrow('Quantity')}
                                </th>
                                <th className="text-right">Avg Cost</th>
                                <th className="text-right">Sale Price</th>
                                <th className="text-right">P&L/Share</th>
                                <th className="text-right">Total Cost (Trade)</th>
                                <th className="text-right">Total Cost (Local)</th>
                                <th className="text-right">Total Proceeds (Trade)</th>
                                <th className="text-right">Total Proceeds (Local)</th>
                                <th className="text-right">P&L (Trade)</th>
                                <th className="text-right">P&L (Local)</th>
                                <th className="text-right">Return % (Trade)</th>
                                <th className="text-right">Return % (Local)</th>
                                <th className="text-center">Currency</th>
                                <th className="text-center">FX Rate</th>
                                <th className="text-center">Asset Class</th>
                                <th className="text-center">Sub Category</th>
                                <th className="text-center">Country</th>
                                 <th className="text-center" onClick={() => handleSort('TradeDate')} style={{cursor: 'pointer', fontWeight: sortColumn === 'TradeDate' ? 'bold' : 'normal', userSelect: 'none'}}>
                                  Date{getSortArrow('TradeDate')}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {sortTrades(getFilteredTrades()).map((trade, index) => {
                                const lotInfo = getLotInfoForTrade(trade);                               
                              if (trade['Buy/Sell'] === 'SELL' && trade.Symbol.includes('NKE')) {
                            
                            }
                                const isSell = trade['Buy/Sell'] === 'SELL';
                                
                                return (
                                  <tr key={trade.TradeID}>
                                    <td>{trade.Symbol}</td>
                                    <td>
                                      <span style={{
                                        color: trade['Buy/Sell'] === 'BUY' ? 'var(--color-success)' : 'var(--color-danger)',
                                        fontWeight: 'var(--font-weight-medium)'
                                      }}>
                                        {trade['Buy/Sell']}
                                      </span>
                                      {/* Lot Details inline */}
                                      {lotInfo && (
                                        <div style={{ fontSize: '0.75em', opacity: 0.7, marginTop: '2px' }}>
                                          {lotInfo.type === 'created' 
                                            ? `→ Created ${lotInfo.lotId}`
                                            : lotInfo.type === 'orphaned'
                                            ? <span style={{ color: 'var(--color-warning)' }} title={lotInfo.tooltip}>
                                                {lotInfo.message}
                                              </span>
                                            : lotInfo.transactions.map((tx, idx) => (
                                                <div key={idx}>
                                                  • Sold {tx.quantity} from {tx.lotId}<br/>
                                                  <span style={{ marginLeft: '10px' }}>
                                                    Bought: {formatDateForDisplay(tx.purchaseDate)} @ {formatCurrency(tx.purchasePrice, trade.CurrencyPrimary)}
                                                  </span>
                                                </div>
                                              ))
                                          }
                                        </div>
                                      )}
                                    </td>
                                    <td className="text-right">{Math.abs(parseFloat(getDisplayQuantity(trade)))}</td>
                                    
                                    {/* Avg Cost */}
                                    <td className="text-right">
                                      {isSell && lotInfo?.transactions?.[0] 
                                        ? formatCurrency(lotInfo.transactions[0].purchasePrice, trade.CurrencyPrimary)
                                        : (trade['Buy/Sell'] === 'BUY' ? formatCurrency(parseFloat(trade.TradePrice), trade.CurrencyPrimary) : '-')}
                                    </td>
                                    
                                    {/* Sale Price */}
                                    <td className="text-right">
                                      {isSell ? formatCurrency(parseFloat(trade.TradePrice), trade.CurrencyPrimary) : '-'}
                                    </td>
                                    
                                    {/* P&L/Share */}
                                    <td className="text-right">
                                      {isSell && lotInfo?.transactions?.[0] && lotInfo.transactions[0].gainLossPerShare !== undefined ? (
                                        <span style={{ color: lotInfo.transactions[0].gainLossPerShare >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
                                          {formatCurrency(lotInfo.transactions[0].gainLossPerShare, trade.CurrencyPrimary)}
                                        </span>
                                      ) : '-'}
                                    </td>
                                    
                                    {/* Total Cost (Trade) */}
                                    <td className="text-right">
                                      {isSell && lotInfo?.transactions 
                                        ? formatCurrency(lotInfo.transactions.reduce((sum, tx) => sum + tx.totalCost, 0), trade.CurrencyPrimary)
                                        : formatCurrency(Math.abs(parseFloat(trade.TradeMoney)), trade.CurrencyPrimary)}
                                    </td>
                                    
                                    {/* Total Cost (Local) */}
                                  <td className="text-right">
                                    {isSell && lotInfo?.transactions 
                                      ? formatCurrency(lotInfo.transactions.reduce((sum, tx) => sum + tx.totalCostBase, 0), userCurrency)
                                      : formatCurrency(Math.abs(parseFloat(trade.TradeMoneyBase || 0)), userCurrency)}
                                  </td>
                                    
                                    {/* Total Proceeds (Trade) */}
                                    <td className="text-right">
                                      {isSell && lotInfo?.transactions 
                                        ? formatCurrency(lotInfo.transactions.reduce((sum, tx) => sum + tx.totalProceeds, 0), trade.CurrencyPrimary)
                                        : '-'}
                                    </td>
                                    
                                    {/* Total Proceeds (Local) */}
                                    <td className="text-right">
                                      {isSell && lotInfo?.transactions 
                                        ? formatCurrency(lotInfo.transactions.reduce((sum, tx) => sum + tx.totalProceedsBase, 0), userCurrency)
                                        : '-'}
                                    </td>
                                    
                                    {/* P&L (Trade) */}
                                    <td className="text-right">
                                      {isSell && lotInfo?.transactions ? (
                                        <span style={{ color: lotInfo.transactions.reduce((sum, tx) => sum + tx.realGainLoss, 0) >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
                                          {formatCurrency(lotInfo.transactions.reduce((sum, tx) => sum + tx.realGainLoss, 0), trade.CurrencyPrimary)}
                                        </span>
                                      ) : '-'}
                                    </td>
                                    
                                    {/* P&L (Local) */}
                                    <td className="text-right">
                                      {isSell && lotInfo?.transactions ? (
                                        <span style={{ color: lotInfo.transactions.reduce((sum, tx) => sum + tx.realGainLossBase, 0) >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
                                          {formatCurrency(lotInfo.transactions.reduce((sum, tx) => sum + tx.realGainLossBase, 0), userCurrency)}
                                        </span>
                                      ) : '-'}
                                    </td>
                                    
                                    {/* Return % (Trade) */}
                                    <td className="text-right">
                                      {isSell && lotInfo?.transactions ? (
                                        <span style={{ color: lotInfo.transactions[0].realGainLossPercent >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
                                          {lotInfo.transactions[0].realGainLossPercent.toFixed(1)}%
                                        </span>
                                      ) : '-'}
                                    </td>
                                    
                                    {/* Return % (Local) - Calculate based on local P&L */}
                                    <td className="text-right">
                                    {isSell && lotInfo?.transactions ? (
                                      <span style={{ 
                                        color: lotInfo.transactions.reduce((sum, tx) => sum + tx.realGainLossBase, 0) >= 0 ? 'var(--color-success)' : 'var(--color-danger)' 
                                      }}>
                                        {((lotInfo.transactions.reduce((sum, tx) => sum + tx.realGainLossBase, 0) / 
                                          lotInfo.transactions.reduce((sum, tx) => sum + tx.totalCostBase, 0)) * 100).toFixed(1)}%
                                      </span>
                                    ) : '-'}
                                  </td>                                    
                                    
                                    {/* Currency */}
                                    <td className="text-center">{trade.CurrencyPrimary}</td>
                                    
                                    {/* FX Rate */}
                                    <td className="text-center">{parseFloat(trade.FXRateToBase).toFixed(5)}</td>
                                    
                                    {/* Asset Class */}
                                    <td className="text-center">{trade.AssetClass || '-'}</td>
                                    
                                    {/* Type */}
                                    <td className="text-center">{trade.SubCategory || '-'}</td>
                                    
                                    {/* Country */}
                                    <td className="text-center">{trade.IssuerCountryCode || '-'}</td>
                                    <td>{formatDateForDisplay(trade.TradeDate)}</td>
                                  </tr>
                                );
                              })}
                              </tbody>
                              </table>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}        
                    {/* Baseline Tab */}
                        {activeTab === 3 && (
                        <div>
                          {/* Check for required data - only positions needed for baseline */}
                          {!positionsData.length ? (
                           <EmptyState 
                              icon="📅" 
                              message="Upload your December 31, 2025 positions to establish your baseline. Not required if you started investing in 2026."
                            />
                          ) : (
                            <>
                            {/* Add header and description */}
                            <div style={{marginBottom: 'var(--space-4)'}}>
                              <h3 style={{marginTop: 0, marginBottom: 'var(--space-2)'}}>
                                December 31, 2025 Baseline
                              </h3>
                              <p style={{
                                color: 'var(--color-text-secondary)',
                                fontSize: '0.95em',
                                marginBottom: 'var(--space-2)'
                              }}>
                                Your positions snapshot used for Belgian CGT calculations. 
                                This Dec 31, 2025 value serves as the baseline for all pre-2026 positions under the transition rules.
                              </p>
                              <p style={{
                                  color: 'var(--color-text-secondary)', 
                                  fontSize: '0.9em',  
                                  fontStyle: 'italic',  
                                  marginBottom: 0
                                }}>
                                Currency conversion notice: Since this is a point-in-time snapshot, 
                                all amounts use the exchange rate from the report date. Historical EUR values are estimates only.</p>
                               </div>

                            {/* Check if no valid positions exist */}
                            {sortPositions(positionsData).filter(position =>
                              position.Symbol && position.Symbol.trim() !== ''
                            ).length === 0 ? (
                              <div style={{
                                textAlign: 'center',
                                padding: 'var(--space-6)',
                                backgroundColor: 'var(--color-background-secondary)',
                                borderRadius: 'var(--radius-lg)',
                                margin: 'var(--space-4) 0'
                              }}>
                                <div style={{ fontSize: '2.5em', marginBottom: 'var(--space-3)', opacity: 0.5 }}>📊</div>
                                <h4 style={{ marginBottom: 'var(--space-2)' }}>No Baseline Positions Found</h4>
                                <p style={{
                                  color: 'var(--color-text-secondary)',
                                  marginBottom: 0
                                }}>
                                  Upload your December 31, 2025 positions to establish your baseline and explore your tax implications.
                                </p>
                              </div>
                            ) : (
                              <div>
                          {/* Baseline Summary */}
                              <div className="overview-metrics-grid" style={{
                                marginBottom: 'var(--space-4)',
                                padding: 'var(--space-3)',
                                backgroundColor: 'var(--color-background-secondary)',
                                borderRadius: 'var(--radius-lg)',
                              }}>
                                <div>
                                  <div style={{fontSize: '0.9em', color: 'var(--color-text-secondary)'}}>
                                    Total Positions
                                  </div>
                                  <div style={{fontSize: '1.3em', fontWeight: 'bold'}}>
                                    {positionsData.filter(p => p.Symbol && p.Symbol.trim() !== '').length}
                                  </div>
                                </div>
                                <div>
                                  <div style={{fontSize: '0.9em', color: 'var(--color-text-secondary)'}}>
                                    Baseline Value
                                  </div>
                                  <div style={{fontSize: '1.3em', fontWeight: 'bold'}}>
                                    {formatCurrency(positionsData
                                      .filter(p => p.Symbol && p.Symbol.trim() !== '')
                                      .reduce((sum, p) => sum + parseFloat(p.PositionValueBase || 0), 0), userCurrency)}
                                  </div>
                                </div>
                                <div>
                                  <div style={{fontSize: '0.9em', color: 'var(--color-text-secondary)'}}>
                                    Total Cost Basis
                                  </div>
                                  <div style={{fontSize: '1.3em', fontWeight: 'bold'}}>
                                    {formatCurrency(positionsData
                                      .filter(p => p.Symbol && p.Symbol.trim() !== '')
                                      .reduce((sum, p) => sum + parseFloat(p.CostBasisMoneyBase || 0), 0), userCurrency)}
                                  </div>
                                </div>
                                <div>
                                  <div style={{fontSize: '0.9em', color: 'var(--color-text-secondary)'}}>
                                    Import Date
                                  </div>
                                  <div style={{fontSize: '1.3em', fontWeight: 'bold'}}>
                                    {(() => {
                                      const savedData = localStorage.getItem('taxme_positions');
                                      if (savedData) {
                                        const data = JSON.parse(savedData);
                                        const isoDate = data.importDate;
                                        const date = new Date(isoDate);
                                        const ibFormat = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
                                        return formatDateForDisplay(ibFormat);
                                      }
                                      return '-';
                                    })()}
                                  </div>
                                </div>
                              </div>

                            {/* Scrollable Table Container */}
                            <div className="table-container">
                              <div className="table-scroll">
                                <table className="data-table">
                                  <thead>
                                <tr>
                                    <th
                                    onClick={() => handleSort('Symbol')}
                                    style={{
                                      cursor: 'pointer',
                                      fontWeight: sortColumn === 'Symbol' ? 'bold' : 'normal',
                                      userSelect: 'none'
                                    }}
                                  >
                                    Instrument{getSortArrow('Symbol')}
                                  </th>
                                  <th
                                    className="text-right"
                                    onClick={() => handleSort('Quantity')}
                                    style={{
                                      cursor: 'pointer',
                                      fontWeight: sortColumn === 'Quantity' ? 'bold' : 'normal',
                                      userSelect: 'none'
                                    }}
                                  >
                                    Quantity{getSortArrow('Quantity')}
                                  </th>
                                  <th
                                    className="text-right"
                                    onClick={() => handleSort('CostBasisPrice')}
                                    style={{
                                      cursor: 'pointer',
                                      fontWeight: sortColumn === 'CostBasisPrice' ? 'bold' : 'normal',
                                      userSelect: 'none'
                                    }}
                                  >
                                    Avg Cost{getSortArrow('CostBasisPrice')}
                                  </th>
                                  <th
                                    className="text-right"
                                    onClick={() => handleSort('MarkPrice')}
                                    style={{
                                      cursor: 'pointer',
                                      fontWeight: sortColumn === 'MarkPrice' ? 'bold' : 'normal',
                                      userSelect: 'none'
                                    }}
                                  >
                                    Baseline Price{getSortArrow('MarkPrice')}
                                  </th>
                                  <th className="text-right">P&L/Share</th>
                                  <th
                                    className="text-right"
                                    onClick={() => handleSort('CostBasisMoney')}
                                    style={{
                                      cursor: 'pointer',
                                      fontWeight: sortColumn === 'CostBasisMoney' ? 'bold' : 'normal',
                                      userSelect: 'none'
                                    }}
                                  >
                                    Total Cost (Trade){getSortArrow('CostBasisMoney')}
                                  </th>
                                  <th
                                    className="text-right"
                                    onClick={() => handleSort('CostBasisMoneyBase')}
                                    style={{
                                      cursor: 'pointer',
                                      fontWeight: sortColumn === 'CostBasisMoneyBase' ? 'bold' : 'normal',
                                      userSelect: 'none'
                                    }}
                                  >
                                    Total Cost (Local){getSortArrow('CostBasisMoneyBase')}
                                  </th>
                                  <th
                                    className="text-right"
                                    onClick={() => handleSort('PositionValue')}
                                    style={{
                                      cursor: 'pointer',
                                      fontWeight: sortColumn === 'PositionValue' ? 'bold' : 'normal',
                                      userSelect: 'none'
                                    }}
                                  >
                                    Baseline Value (Trade){getSortArrow('PositionValue')}
                                  </th>
                                  <th
                                    className="text-right"
                                    onClick={() => handleSort('PositionValueBase')}
                                    style={{
                                      cursor: 'pointer',
                                      fontWeight: sortColumn === 'PositionValueBase' ? 'bold' : 'normal',
                                      userSelect: 'none'
                                    }}
                                  >
                                    Baseline Value (Local){getSortArrow('PositionValueBase')}
                                  </th>
                                  <th className="text-right">Unrealized P&L (Trade)</th>
                                  <th className="text-right">Unrealized P&L (Local)</th>
                                  <th className="text-right">Unrealized Return % (Trade)</th>
                                  <th className="text-right">Unrealized Return % (Local)</th>
                                  <th
                                    className="text-center"
                                    onClick={() => handleSort('CurrencyPrimary')}
                                    style={{
                                      cursor: 'pointer',
                                      fontWeight: sortColumn === 'CurrencyPrimary' ? 'bold' : 'normal',
                                      userSelect: 'none'
                                    }}
                                  >
                                    Currency{getSortArrow('CurrencyPrimary')}
                                  </th>
                                  <th
                                    className="text-center"
                                    onClick={() => handleSort('FXRateToBase')}
                                    style={{
                                      cursor: 'pointer',
                                      fontWeight: sortColumn === 'FXRateToBase' ? 'bold' : 'normal',
                                      userSelect: 'none'
                                    }}
                                  >
                                    FX Rate{getSortArrow('FXRateToBase')}
                                  </th>
                                  <th
                                    className="text-center"
                                    onClick={() => handleSort('AssetClass')}
                                    style={{
                                      cursor: 'pointer',
                                      fontWeight: sortColumn === 'AssetClass' ? 'bold' : 'normal',
                                      userSelect: 'none'
                                    }}
                                  >
                                    Asset Class{getSortArrow('AssetClass')}
                                  </th>
                                  <th
                                    className="text-center"
                                    onClick={() => handleSort('SubCategory')}
                                    style={{
                                      cursor: 'pointer',
                                      fontWeight: sortColumn === 'SubCategory' ? 'bold' : 'normal',
                                      userSelect: 'none'
                                    }}
                                  >
                                    Sub Category{getSortArrow('SubCategory')}
                                  </th>
                                  <th
                                    className="text-center"
                                    onClick={() => handleSort('IssuerCountryCode')}
                                    style={{
                                      cursor: 'pointer',
                                      fontWeight: sortColumn === 'IssuerCountryCode' ? 'bold' : 'normal',
                                      userSelect: 'none'
                                    }}
                                  >
                                    Country{getSortArrow('IssuerCountryCode')}
                                  </th>
                                 <th
                                    className="text-center"
                                    onClick={() => handleSort('ReportDate')}
                                    style={{
                                      cursor: 'pointer',
                                      fontWeight: sortColumn === 'ReportDate' ? 'bold' : 'normal',
                                      userSelect: 'none'
                                    }}
                                  >
                                    Date{getSortArrow('ReportDate')}
                                  </th>
                                  </tr>
                              </thead>
                              <tbody>
                                {sortPositions(positionsData)
                                  .filter(position => position.Symbol && position.Symbol.trim() !== '')
                                  .map((position, index) => {
                                    // Calculate P&L metrics
                                    const plPerShare = parseFloat(position.MarkPrice || 0) - parseFloat(position.CostBasisPrice || 0);
                                    const unrealizedPL = parseFloat(position.PositionValue || 0) - parseFloat(position.CostBasisMoney || 0);
                                    const unrealizedPLBase = parseFloat(position.PositionValueBase || 0) - parseFloat(position.CostBasisMoneyBase || 0);
                                    const unrealizedReturnTrade = parseFloat(position.CostBasisMoney || 0) > 0 
                                      ? (unrealizedPL / parseFloat(position.CostBasisMoney || 0)) * 100 : 0;
                                    const unrealizedReturnLocal = parseFloat(position.CostBasisMoneyBase || 0) > 0 
                                      ? (unrealizedPLBase / parseFloat(position.CostBasisMoneyBase || 0)) * 100 : 0;
                                    
                                    return (
                                      <tr key={position.Symbol}>
                                        <td>{position.Symbol}</td>
                                        <td className="text-right">{getDisplayQuantity(position)}</td>
                                        <td className="text-right">{formatCurrency(position.CostBasisPrice || 0, position.CurrencyPrimary)}</td>
                                        <td className="text-right">{formatCurrency(position.MarkPrice || 0, position.CurrencyPrimary)}</td>
                                        <td className="text-right">
                                          <span style={{
                                            color: plPerShare >= 0 ? 'var(--color-success)' : 'var(--color-danger)'
                                          }}>
                                            {formatCurrency(plPerShare, position.CurrencyPrimary)}
                                          </span>
                                        </td>
                                        <td className="text-right">{formatCurrency(position.CostBasisMoney || 0, position.CurrencyPrimary)}</td>
                                        <td className="text-right">{formatCurrency(position.CostBasisMoneyBase || 0, userCurrency)}</td>
                                        <td className="text-right">{formatCurrency(position.PositionValue || 0, position.CurrencyPrimary)}</td>
                                        <td className="text-right">{formatCurrency(position.PositionValueBase || 0, userCurrency)}</td>
                                        <td className="text-right">
                                          <span style={{
                                            color: unrealizedPL >= 0 ? 'var(--color-success)' : 'var(--color-danger)',
                                            fontWeight: 'var(--font-weight-medium)'
                                          }}>
                                            {formatCurrency(unrealizedPL, position.CurrencyPrimary)}
                                          </span>
                                        </td>
                                        <td className="text-right">
                                          <span style={{
                                            color: unrealizedPLBase >= 0 ? 'var(--color-success)' : 'var(--color-danger)',
                                            fontWeight: 'var(--font-weight-medium)'
                                          }}>
                                            {formatCurrency(unrealizedPLBase, userCurrency)}
                                          </span>
                                        </td>
                                        <td className="text-right">
                                          <span style={{
                                            color: unrealizedReturnTrade >= 0 ? 'var(--color-success)' : 'var(--color-danger)'
                                          }}>
                                            {unrealizedReturnTrade.toFixed(1)}%
                                          </span>
                                        </td>
                                        <td className="text-right">
                                          <span style={{
                                            color: unrealizedReturnLocal >= 0 ? 'var(--color-success)' : 'var(--color-danger)'
                                          }}>
                                            {unrealizedReturnLocal.toFixed(1)}%
                                          </span>
                                        </td>
                                        <td className="text-center">{position.CurrencyPrimary}</td>
                                        <td className="text-center">{position.FXRateToBase}</td>
                                        <td className="text-center">{position.AssetClass}</td>
                                        <td className="text-center">{position.SubCategory}</td>
                                        <td className="text-center">{position.IssuerCountryCode}</td>
                                        <td>{formatDateForDisplay(position.ReportDate)}</td>
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                        )}
                    </>
                    )}
                    </div>
                        )}                        
                </div>
                </>
                )}
              </main>
              </>
              )}
             {/* Footer - Always visible at bottom */}
                <footer className="app-footer">
                  <div className="footer-content">
                    <p className="footer-text">
                      © 2025 TaxMe - Belgian Capital Gains Tracker v0.9.0-beta. All Rights Reserved.
                      <span className="footer-separator"> | </span>
                      Informational purposes only - not tax advice.
                      <span className="footer-separator"> | </span>
                      By using this site, you accept our Terms and Privacy Policy.
                    </p>
                    <div className="footer-links">
                      <a 
                        href="#help"
                        className="footer-link"
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentView('help');
                          window.scrollTo(0, 0);
                        }}
                      >
                        Help
                      </a>
                      <a 
                        href="#terms"
                        className="footer-link"
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentView('terms');
                          window.scrollTo(0, 0);
                        }}
                      >
                        Terms
                      </a>
                      <a 
                        href="#privacy"
                        className="footer-link"
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentView('privacy');
                          window.scrollTo(0, 0);
                        }}
                      >
                        Privacy
                      </a>
                    </div>
                  </div>
                </footer>
                {/* Contact Form Modal */}
              <ContactFormModal 
                isOpen={showContactForm} 
                onClose={() => setShowContactForm(false)} 
              />
            </div>
          );
        }


export default App;